import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import { useToast } from '../../../components/ui/ToastProvider';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { createDrawConfig } from './MapStyles';
import { initializeMapbox, getMapStyle, DEFAULT_BASE_MAP, MAPBOX_ACCESS_TOKEN, getParcelOverlayConfig, ESRI_REFERENCE_BOUNDARIES_PLACES, ESRI_REFERENCE_TRANSPORTATION } from '../config/mapConfig';
import {
  allParcelLayerIds,
  isParcelTileSourceId,
  overlaySources,
  PARCEL_SOURCE_IDS,
  parcelLayerIds
} from '../utils/parcelSources';
import { PARCEL_PAINT_MIN_ZOOM } from '../utils/parcelZoomPolicy';
import { isTapNotDrag, isParcelTouchTap } from '../utils/parcelPointer';
import { parcelAtPoint, parcelFromFeature, PARCEL_HIT_LAYERS } from '../utils/parcelAtPoint';
import { shouldGateParcelsEnable } from '../../pro/entitlements';
import { applyEsriReferenceOverlays } from '../utils/esriReferenceOverlays';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM, DEFAULT_MAP_PITCH, DEFAULT_MAP_BEARING, createPersistableMapView, normalizePersistedMapView } from '../utils/mapViewPersistence';
import { buildMapPointProperties, applyPointsWindToMap, STAND_POINT_FILTER, MARKER_POINT_FILTER } from '../utils/mapPointProperties';
import { SOIL_TEXTURE_PALETTE, SOIL_DRAINAGE_PALETTE, SOIL_LEGEND_TEXTURE, SOIL_LEGEND_DRAINAGE } from '../soilPalette';
import { applyPlacementToolChrome } from '../utils/mapToolChrome';
import {
  prefersTouchMapGestures,
  applyMobileMapGestureHardening,
  syncMobileMapGesturesFor3D,
  bindDrawDragPanRecovery
} from '../utils/mobileMapGestures';
import { MOBILE_MQ as PARCELS_SHEET_MEDIA_QUERY } from '../../../hooks/useMatchMedia';

const getCssColor = (varName, fallback) => {
  if (typeof document === 'undefined') return fallback;
  try {
    const value = getComputedStyle(document.documentElement).getPropertyValue(varName);
    return value ? value.trim() || fallback : fallback;
  } catch (_) {
    return fallback;
  }
};

const PUBLIC_LAND_COLOR_DEFAULTS = {
  forest: '#2e7d32',
  park: '#6d8f3f',
  blm: '#b8860b',
  state: '#b85933',
  local: '#4c9ea6',
  military: '#cc0000'
};

const PUBLIC_LANDS_COLORS = {
  forest: getCssColor('--color-public-land-forest', PUBLIC_LAND_COLOR_DEFAULTS.forest),
  park: getCssColor('--color-public-land-park', PUBLIC_LAND_COLOR_DEFAULTS.park),
  blm: getCssColor('--color-public-land-blm', PUBLIC_LAND_COLOR_DEFAULTS.blm),
  state: getCssColor('--color-public-land-state', PUBLIC_LAND_COLOR_DEFAULTS.state),
  local: getCssColor('--color-public-land-local', PUBLIC_LAND_COLOR_DEFAULTS.local),
  military: getCssColor('--color-public-land-military', PUBLIC_LAND_COLOR_DEFAULTS.military)
};

const PUBLIC_LANDS_LEGEND = [
  { label: 'USFS / National Forest', color: PUBLIC_LANDS_COLORS.forest },
  { label: 'National Park / USFWS', color: PUBLIC_LANDS_COLORS.park },
  { label: 'BLM', color: PUBLIC_LANDS_COLORS.blm },
  { label: 'State WMAs / Forests', color: PUBLIC_LANDS_COLORS.state },
  { label: 'County / Local', color: PUBLIC_LANDS_COLORS.local },
  { label: 'Military (DoD)', color: PUBLIC_LANDS_COLORS.military }
];

const PUBLIC_LANDS_STYLE_CATEGORY_EXPRESSION = [
  'coalesce',
  ['get', 'style_category'],
  'federal_forest'
];

const PUBLIC_LANDS_COLOR_EXPRESSION = [
  'match',
  PUBLIC_LANDS_STYLE_CATEGORY_EXPRESSION,
  'military', PUBLIC_LANDS_COLORS.military,
  'federal_park', PUBLIC_LANDS_COLORS.park,
  'blm', PUBLIC_LANDS_COLORS.blm,
  'state', PUBLIC_LANDS_COLORS.state,
  'local', PUBLIC_LANDS_COLORS.local,
  'federal_forest', PUBLIC_LANDS_COLORS.forest,
  PUBLIC_LANDS_COLORS.forest
];

const SOIL_TILE_URL = 'https://public-land-tiles.atl1.cdn.digitaloceanspaces.com/soil/soil-2025-01/tiles/AL/{z}/{x}/{y}.pbf';
const SOIL_SOURCE_ID = 'soil';
const SOIL_LAYER_ID = 'soil-fill';
const SOIL_LINE_ID = 'soil-outline';
// Drainage bucket expression using raw drainage_class or fallback hydgrp (aligned with backend mapping)
const SOIL_DRAINAGE_BUCKET = [
  'let',
  'dr',
  ['downcase', ['coalesce', ['get', 'drainage_class'], '']],
  'hg',
  ['upcase', ['coalesce', ['get', 'hydrologic_group'], '']],
  [
    'case',
    ['!=', ['var', 'dr'], ''],
    [
      'match',
      ['var', 'dr'],
      'excessively drained', 'well_drained',
      'somewhat excessively drained', 'well_drained',
      'well drained', 'well_drained',
      'moderately well drained', 'well_drained',
      'somewhat poorly drained', 'moderate',
      'poorly drained', 'poor',
      'very poorly drained', 'poor',
      'unknown'
    ],
    [
      'case',
      // hydgrp fallback (mirror backend): C -> moderate; C/D or D -> poor; A/D or B/D -> moderate; A -> well; B -> moderate
      ['in', 'D', ['split', ['var', 'hg'], '/']], 'poor',
      ['in', 'C/D', ['var', 'hg']], 'poor',
      ['in', 'C', ['split', ['var', 'hg'], '/']], 'moderate',
      ['in', 'A/D', ['var', 'hg']], 'moderate',
      ['in', 'B/D', ['var', 'hg']], 'moderate',
      ['in', 'A', ['split', ['var', 'hg'], '/']], 'well_drained',
      ['in', 'B', ['split', ['var', 'hg'], '/']], 'moderate',
      'unknown'
    ]
  ]
];
const SOIL_DRAINAGE_COLORS = ['match', SOIL_DRAINAGE_BUCKET,
  'well_drained', SOIL_DRAINAGE_PALETTE.well_drained,
  'moderate', SOIL_DRAINAGE_PALETTE.moderate,
  'poor', SOIL_DRAINAGE_PALETTE.poor,
  SOIL_DRAINAGE_PALETTE.fallback
];

// Popup normalization helpers (client-side mirror of backend logic)
const DRAINAGE_LABELS = {
  well_drained: 'Well drained',
  moderate: 'Moderate',
  poor: 'Poor / holds water'
};

const TEXTURE_LABELS = {
  sand: 'Sand',
  sandy_loam: 'Sandy loam',
  loam: 'Loam',
  clay: 'Clay / heavy',
  muck: 'Muck / organic'
};

const normalizeDrainageLocal = (drainageRaw, hydgrpRaw) => {
  const dr = (drainageRaw || '').toLowerCase();
  if (dr.includes('very poorly') || dr.includes('poorly')) return 'poor';
  if (dr.includes('somewhat poorly')) return 'moderate';
  if (dr.includes('moderately well') || dr.includes('well drained') || dr.includes('excessive')) return 'well_drained';
  const hg = (hydgrpRaw || '').replace(/\s+/g, '').toUpperCase();
  if (!hg) return undefined;
  const parts = hg.split(/[/-]/).filter(Boolean);
  const hasA = parts.includes('A');
  const hasB = parts.includes('B');
  const hasC = parts.includes('C');
  const hasD = parts.includes('D');
  if ((hasA || hasB) && hasD) return 'moderate';
  if (hasA) return 'well_drained';
  if (hasB) return 'moderate';
  if (hasC) return 'moderate';
  if (hasD) return 'poor';
  return undefined;
};

const normalizeTextureLocal = (textureRaw) => {
  const tx = (textureRaw || '').toLowerCase();
  if (!tx) return undefined;
  if (tx.includes('muck') || tx.includes('organic') || tx.includes('peat') || tx.includes('histosol')) return 'muck';
  if (tx.includes('smectitic') || tx.includes('vertic') || tx.includes('kaolinitic')) return 'clay';
  if (tx.includes('fine') && !tx.includes('silty') && !tx.includes('loam') && !tx.includes('sandy')) return 'clay';
  if (tx.includes('fine-silty') || tx.includes('silt loam') || tx.includes('silty')) return 'loam';
  if (tx.includes('psamment')) return 'sand';
  if (
    tx.includes('sandy loam') ||
    tx.includes('loamy sand') ||
    tx.includes('sandy-loam') ||
    tx.includes('coarse loamy') ||
    tx.includes('fine sandy loam') ||
    tx.includes('fine-loamy') ||
    tx.includes('loamy fine sand')
  ) return 'sandy_loam';
  if (tx.includes('clay')) return 'clay';
  if (tx.includes('sand') || tx.includes('sandy')) return 'sand';
  if (tx.includes('loam')) return 'loam';
  return undefined;
};

// Texture bucket expression: prefer normalized if present, else fallback heuristics on raw texture_class
const SOIL_TEXTURE_BUCKET = [
  'case',
  ['has', 'texture_class_normalized'], ['coalesce', ['get', 'texture_class_normalized'], 'unknown'],
  [
    'let',
    'tx',
    ['downcase', ['coalesce', ['get', 'texture_class'], '']],
    [
      'case',
      ['any',
        ['>=', ['index-of', 'muck', ['var', 'tx']], 0],
        ['>=', ['index-of', 'organic', ['var', 'tx']], 0],
        ['>=', ['index-of', 'peat', ['var', 'tx']], 0],
        ['>=', ['index-of', 'histosol', ['var', 'tx']], 0]
      ], 'muck',
      ['any',
        ['>=', ['index-of', 'smectitic', ['var', 'tx']], 0],
        ['>=', ['index-of', 'vertic', ['var', 'tx']], 0],
        ['>=', ['index-of', 'kaolinitic', ['var', 'tx']], 0],
        ['>=', ['index-of', 'clay', ['var', 'tx']], 0]
      ], 'clay',
      ['all',
        ['>=', ['index-of', 'fine', ['var', 'tx']], 0],
        ['<', ['index-of', 'silty', ['var', 'tx']], 0],
        ['<', ['index-of', 'loam', ['var', 'tx']], 0],
        ['<', ['index-of', 'sandy', ['var', 'tx']], 0]
      ], 'clay',
      ['any',
        ['>=', ['index-of', 'fine-silty', ['var', 'tx']], 0],
        ['>=', ['index-of', 'silt loam', ['var', 'tx']], 0],
        ['>=', ['index-of', 'silty', ['var', 'tx']], 0]
      ], 'loam',
      ['any',
        ['>=', ['index-of', 'sandy loam', ['var', 'tx']], 0],
        ['>=', ['index-of', 'loamy sand', ['var', 'tx']], 0],
        ['>=', ['index-of', 'sandy-loam', ['var', 'tx']], 0],
        ['>=', ['index-of', 'coarse loamy', ['var', 'tx']], 0],
        ['>=', ['index-of', 'fine sandy loam', ['var', 'tx']], 0],
        ['>=', ['index-of', 'fine-loamy', ['var', 'tx']], 0],
        ['>=', ['index-of', 'loamy fine sand', ['var', 'tx']], 0]
      ], 'sandy_loam',
      ['any',
        ['>=', ['index-of', 'sand', ['var', 'tx']], 0],
        ['>=', ['index-of', 'psamment', ['var', 'tx']], 0]
      ], 'sand',
      ['>=', ['index-of', 'loam', ['var', 'tx']], 0], 'loam',
      'unknown'
    ]
  ]
];

const SOIL_TEXTURE_COLORS = ['match', SOIL_TEXTURE_BUCKET,
  'sand', SOIL_TEXTURE_PALETTE.sand,
  'sandy_loam', SOIL_TEXTURE_PALETTE.sandy_loam,
  'loam', SOIL_TEXTURE_PALETTE.loam,
  'clay', SOIL_TEXTURE_PALETTE.clay,
  'muck', SOIL_TEXTURE_PALETTE.muck,
  SOIL_TEXTURE_PALETTE.fallback
];
// Legends are imported from soilPalette

let popupStylesInjected = false;
const ensurePopupStyles = () => {
  if (popupStylesInjected) return;
  if (typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.setAttribute('data-yona-popup', 'true');
  style.textContent = `
    .mapboxgl-popup.yona-popup, .yona-popup .mapboxgl-popup-content, .mapboxgl-popup.yona-popup .mapboxgl-popup-content {
      background: transparent;
      box-shadow: none;
      padding: 0;
      border-radius: 0;
    }
    .mapboxgl-popup.yona-popup .mapboxgl-popup-tip {
      display: none;
    }
    .mapboxgl-popup-content {
      padding: 0;
    }
    .mapboxgl-popup.yona-popup .mapboxgl-popup-close-button {
      color: #e2e8f0;
      font-size: 14px;
      top: 6px;
      right: 8px;
    }
  `;
  document.head.appendChild(style);
  popupStylesInjected = true;
};

// Saved-feature layers that own a tap, except while Identify mode is armed.
const FEATURE_CLICK_LAYERS = ['features-polygons', 'features-polygons-outline', 'features-lines', 'features-points', 'features-markers', 'features-stands'];

/** Detach the Identify tap listeners recorded on the map instance, if any. */
const detachParcelIdentifyHandlers = (mapInstance) => {
  try {
    const handlers = mapInstance && mapInstance.__yona_regridHandlers && mapInstance.__yona_regridHandlers.identify;
    if (!handlers || typeof mapInstance.off !== 'function') return;
    mapInstance.off('touchstart', handlers.onIdentifyTouchStart);
    mapInstance.off('touchend', handlers.onIdentifyTouchEnd);
    mapInstance.__yona_regridHandlers.identify = null;
  } catch (_) {}
};

/**
 * Record what the last Identify tap saw, for the ?parcel_debug=1 readout. A tap
 * that fires but finds nothing is otherwise indistinguishable from a tap that
 * never fired, which is impossible to tell apart on a phone.
 */
const recordParcelTapDebug = (mapInstance, point, parcel) => {
  try {
    mapInstance.__yona_parcelTapDebug = {
      at: new Date().toLocaleTimeString(),
      point: point ? { x: Math.round(point.x), y: Math.round(point.y) } : null,
      zoom: mapInstance.getZoom ? Number(mapInstance.getZoom().toFixed(2)) : null,
      layers: PARCEL_HIT_LAYERS.filter((id) => mapInstance.getLayer && mapInstance.getLayer(id)),
      hit: !!parcel,
      parcelId: parcel ? parcel.parcelId : null
    };
  } catch (_) {}
};

const buildPopupHtml = (title, rows = []) => {
  const safe = (v) => (v === undefined || v === null ? '' : String(v));
  const rowHtml = rows
    .filter(r => r && r.label)
    .map(r => {
      const label = safe(r.label);
      const value = safe(r.value) || '—';
      return `<div style="display:flex;justify-content:space-between;gap:8px;">
        <span style="color:#94a3b8;font-size:12px;">${label}</span>
        <span style="color:#e2e8f0;font-size:12px;font-weight:600;">${value}</span>
      </div>`;
    }).join('');
  const titleHtml = title ? `<div style="font-weight:700;font-size:13px;color:#f8fafc;margin-bottom:6px;">${safe(title)}</div>` : '';
  return `
    <div style="background:#0f172a;border:1px solid #1e293b;border-radius:10px;padding:10px;min-width:180px;max-width:260px;box-shadow:0 10px 24px rgba(0,0,0,0.35);">
      ${titleHtml}
      <div style="display:flex;flex-direction:column;gap:4px;">
        ${rowHtml}
      </div>
    </div>`;
};

/**
 * BaseMap - Centralized map component for both personal and club maps
 * 
 * Props:
 * @param {Object} mapConfig - Map configuration object
 * @param {Array} features - Array of features to display on the map
 * @param {Function} onMapReady - Callback when map is ready
 * @param {Function} onFeatureClick - Callback when a feature is clicked
 * @param {Function} onMapMove - Callback when map moves
 * @param {Function} onDrawCreate - Callback when a feature is drawn
 * @param {Function} onDrawUpdate - Callback when a feature is updated
 * @param {Function} onDrawDelete - Callback when a feature is deleted
 * @param {Function} onFeaturesRendered - Callback when features are rendered
 * @param {string} baseMapStyle - Base map style to use
 * @param {Array} layers - Additional layers to add
 * @param {Object} initialView - Initial map view settings
 * @param {string} drawingMode - Current drawing mode ('select', 'polygon', 'line', 'point')
 */
export function BaseMap({
  mapConfig = {},
  features = [],
  onMapReady,
  onFeatureClick,
  onParcelInfoRequest,
  parcelSelectionEnabled = false,
  onParcelSelectToggle,
  onRequestParcelBoundaryCreate,
  onExitParcelSelection,
  closeParcelsPopoverSignal = 0,
  hasParcelSelection = false,
  isParcelCreateBusy = false,
  selectedParcelIds = [],
  previewUnionGeometry = null,
  onMapMove,
  onDrawCreate,
  onDrawUpdate,
  onDrawDelete,
  onFeaturesRendered,
  baseMapStyle = DEFAULT_BASE_MAP,
  layers = [],
  initialView = {},
  drawingMode = 'select',
  currentWindDegrees = null,
  onStartDropWaypoint,
  onStartDropMarker,
  onStartPlaceStand,
  canDraw = true,
  blockedDrawMessage = 'Only presidents can add to the club map.',
  debug = false,
  /** Club map: OPEN / ON STAND enamel pin colors */
  fieldEditionStandPins = false,
  /** When false: omit the Layers drawer (labels / parcels / public land / soil). */
  showParcelsControl = true,
  /** Pro entitlement for parcel boundaries + owner sheet. Fail closed. */
  canEnableParcels = false,
  onParcelsProRequired,
  /** Club land-setup “Select parcels”: turn the overlay on from the parent. */
  forceParcelsOn = false,
  /** Phone Identify mode: a tap resolves to the parcel, not to a saved feature. */
  parcelIdentifyEnabled = false,
  /** Reports the Parcels overlay on/off so the parent can offer Identify. */
  onParcelsVisibilityChange,
}) {
  const onDrawModeChangeRef = useRef(undefined);
  useEffect(() => { onDrawModeChangeRef.current = mapConfig?.onDrawModeChange || null; }, [mapConfig]);
  const mapContainer = useRef(null);
  const map = useRef(null);
  const draw = useRef(null);
  const moveEndTimer = useRef(null);
  const featuresRef = useRef(features);
  const terrainAddedRef = useRef(false);
  const initializedRef = useRef(false);
  const markerHoverBoundRef = useRef(false);
  const hoverPopupRef = useRef(null);
  const customCtrlRef = useRef(null);
  const parcelsCtrlRef = useRef(null);
  const publicLandsClickHandlerRef = useRef(null);
  const soilClickHandlerRef = useRef(null);
  const soilPopupRef = useRef(null);
  const canDrawRef = useRef(canDraw);
  const drawGuardBoundRef = useRef(false);
  const didInitialFadeRef = useRef(false);
  const lastTouchHandledAtRef = useRef(0);
  const touchStartRef = useRef(null);
  const featureTouchHandlersRef = useRef(null);
  const currentWindDegreesRef = useRef(currentWindDegrees);
  const [parcelsCtrlAdded, setParcelsCtrlAdded] = useState(false);
  
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [windArrowDelayPassed, setWindArrowDelayPassed] = useState(false);
  const [showRegridParcels, setShowRegridParcels] = useState(false);
  const showRegridParcelsRef = useRef(false);
  showRegridParcelsRef.current = showRegridParcels;
  const requestParcelsToggle = useCallback((next) => {
    const current = showRegridParcelsRef.current;
    const turningOn = typeof next === 'boolean' ? !!next : !current;
    if (shouldGateParcelsEnable(canEnableParcels, turningOn)) {
      if (typeof onParcelsProRequired === 'function') onParcelsProRequired();
      return;
    }
    setShowRegridParcels(turningOn);
  }, [canEnableParcels, onParcelsProRequired]);
  const requestParcelsToggleRef = useRef(requestParcelsToggle);
  requestParcelsToggleRef.current = requestParcelsToggle;

  useEffect(() => {
    if (canEnableParcels !== true && showRegridParcels) {
      setShowRegridParcels(false);
    }
  }, [canEnableParcels, showRegridParcels]);
  useEffect(() => {
    if (forceParcelsOn && canEnableParcels === true && !showRegridParcelsRef.current) {
      requestParcelsToggle(true);
    }
  }, [forceParcelsOn, canEnableParcels, requestParcelsToggle]);
  const [showReferenceLabels, setShowReferenceLabels] = useState(true);
  const [is3D, setIs3D] = useState(false);
  const [showPublicLands, setShowPublicLands] = useLocalStorage('publicLandsOn', false);
  const [showSoil, setShowSoil] = useLocalStorage('soilLayerOn', false);
  const [soilColorMode, setSoilColorMode] = useLocalStorage('soilColorMode', 'drainage'); // 'drainage' | 'texture'
  const showSoilRef = useRef(showSoil);
  const soilColorModeRef = useRef(soilColorMode);
  const [savedMapView, setSavedMapView] = useLocalStorage('mapView.v1', null);
  const normalizedSavedMapView = useMemo(() => normalizePersistedMapView(savedMapView), [savedMapView]);
  const savedViewPayload = normalizedSavedMapView?.view;
  const savedViewIsFresh = normalizedSavedMapView?.isFresh;
  const hoveredParcelIdRef = useRef(null);
  const selectedParcelIdRef = useRef(null);
  const { show: showToast } = useToast();
  const AOI_GATING_ENABLED = (process.env.REACT_APP_PARCELS_AOI_GATING || 'false') === 'true';
  const TILE_GUARD_ENABLED = (process.env.REACT_APP_TILE_GUARD || 'true') !== 'false';

  useEffect(() => { showSoilRef.current = showSoil; }, [showSoil]);
  useEffect(() => { soilColorModeRef.current = soilColorMode; }, [soilColorMode]);

  // Keep latest draw handlers to avoid stale closures across route switches
  const onDrawCreateRef = useRef(onDrawCreate);
  const onDrawUpdateRef = useRef(onDrawUpdate);
  const onDrawDeleteRef = useRef(onDrawDelete);
  useEffect(() => { onDrawCreateRef.current = onDrawCreate; }, [onDrawCreate]);
  useEffect(() => { onDrawUpdateRef.current = onDrawUpdate; }, [onDrawUpdate]);
  useEffect(() => { onDrawDeleteRef.current = onDrawDelete; }, [onDrawDelete]);

  const ENABLE_WIND_WEDGES = false; // Disabled wedges
  const ENABLE_WIND_HALO_ARROW = true; // Enable single halo + arrow

  // Keep canDraw reactive inside event handlers
  useEffect(() => {
    canDrawRef.current = canDraw;
  }, [canDraw]);

  // Update features ref when features change
  useEffect(() => {
    featuresRef.current = features;
  }, [features]);

  useEffect(() => {
    currentWindDegreesRef.current = currentWindDegrees;
  }, [currentWindDegrees]);

  // Initialize Mapbox with environment token
  useEffect(() => {
    initializeMapbox();
  }, []);

  // Delay showing wind arrow a couple seconds to allow weather fetch
  useEffect(() => {
    const timer = setTimeout(() => setWindArrowDelayPassed(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  const { usePersonalSavedView: _upsvFlag, ...mapConfigForMapbox } = mapConfig;
  const usePersonalSavedView = mapConfig.usePersonalSavedView !== false;

  // Default map configuration - centered on Alabama for southern states view
  const defaultMapConfig = {
    center: mapConfig.center
      || (usePersonalSavedView && savedViewIsFresh ? savedViewPayload?.center : null)
      || DEFAULT_MAP_CENTER,
    zoom: mapConfig.zoom
      ?? (usePersonalSavedView && savedViewIsFresh ? savedViewPayload?.zoom : undefined)
      ?? DEFAULT_MAP_ZOOM,
    pitch: mapConfig.pitch
      ?? (usePersonalSavedView && savedViewIsFresh ? savedViewPayload?.pitch : undefined)
      ?? DEFAULT_MAP_PITCH,
    bearing: mapConfig.bearing
      ?? (usePersonalSavedView && savedViewIsFresh ? savedViewPayload?.bearing : undefined)
      ?? DEFAULT_MAP_BEARING,
    // Allow up to 22 to keep raster layer visible while overscaling tiles
    maxZoom: mapConfig.maxZoom ?? 22,
    minZoom: mapConfig.minZoom ?? 0,
    antialias: mapConfig.antialias ?? true, // Enable antialiasing for better 3D rendering
    ...mapConfigForMapbox
  };

  // Get the appropriate map style
  const mapStyle = getMapStyle(baseMapStyle);

  // (removed unused helpers)

  // Initialize map - ONLY ONCE
  useEffect(() => {
    if (!mapContainer.current || initializedRef.current) return;

    console.log('Initializing new map instance');
    initializedRef.current = true;

    const persistMapView = () => {
      if (!map.current) return;
      const candidate = createPersistableMapView({
        center: map.current.getCenter(),
        zoom: map.current.getZoom(),
        pitch: map.current.getPitch(),
        bearing: map.current.getBearing()
      });
      if (!candidate) return;
      const payload = { view: candidate, savedAt: Date.now() };
      setSavedMapView(payload);
    };

    // Always construct handlers enabled so later enable() works reliably on mobile.
    // Touch 2D then hardens via applyMobileMapGestureHardening; 3D re-enables via sync.
    const touchMapGestures = prefersTouchMapGestures();
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapStyle,
      ...defaultMapConfig,
      touchZoomRotate: true,
      touchPitch: true,
      dragRotate: true,
      cooperativeGestures: false
    });
    try { ensurePopupStyles(); } catch (_) {}
    try {
      map.current.__yona_initialViewStrategy = mapConfig.center
        ? 'club'
        : (usePersonalSavedView && savedViewIsFresh ? 'saved' : 'default');
    } catch (_) {}
    try { map.current.__yona_touchMapGestures = touchMapGestures; } catch (_) {}

    if (touchMapGestures) {
      try { applyMobileMapGestureHardening(map.current); } catch (_) {}
    }
    // Draw can leave dragPan disabled on touch after mode/selection changes; recover safely.
    const unbindDrawDragPanRecovery = bindDrawDragPanRecovery(map.current);

    // Add navigation controls with 3D support (guard for tests)
    if (map.current && typeof map.current.addControl === 'function') {
      map.current.addControl(new mapboxgl.NavigationControl({
        showCompass: true,
        showZoom: true,
        visualizePitch: true
      }), 'top-right');
    }

    // Always add drawing tools so polygon and line controls stay visible
    draw.current = new MapboxDraw(createDrawConfig());
    if (map.current && typeof map.current.addControl === 'function') {
      map.current.addControl(draw.current);
    }

    // Guard clicks on Draw toolbar buttons for non-presidents; add touch path so buttons work on mobile
    const bindDrawToolbarGuards = () => {
      if (!mapContainer.current || drawGuardBoundRef.current) return;
      try {
        const root = mapContainer.current.parentElement || mapContainer.current;
        const selectors = [
          '.mapbox-gl-draw_polygon',
          '.mapbox-gl-draw_line',
          '.mapbox-gl-draw_line_string'
        ];
        const buttons = root.querySelectorAll(selectors.join(','));
        if (!buttons || buttons.length === 0) return;
        const clickGuard = (e) => {
          if (!canDrawRef.current) {
            try { e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation && e.stopImmediatePropagation(); } catch(_) {}
            try { alert(blockedDrawMessage); } catch(_) {}
            return false;
          }
          return true;
        };
        const touchModeForButton = (btn) => {
          if (btn.classList.contains('mapbox-gl-draw_polygon')) return 'draw_polygon';
          if (btn.classList.contains('mapbox-gl-draw_line') || btn.classList.contains('mapbox-gl-draw_line_string')) return 'draw_line_string';
          return null;
        };
        const touchHandler = (e) => {
          if (!canDrawRef.current) {
            try { e.preventDefault(); } catch(_) {}
            try { alert(blockedDrawMessage); } catch(_) {}
            return;
          }
          const mode = touchModeForButton(e.currentTarget);
          if (mode && draw.current && typeof draw.current.changeMode === 'function') {
            try { e.preventDefault(); } catch(_) {}
            draw.current.changeMode(mode);
          }
        };
        buttons.forEach(btn => {
          try {
            btn.addEventListener('click', clickGuard, true);
            btn.addEventListener('touchend', touchHandler, { passive: false });
            if (!canDrawRef.current) {
              btn.title = blockedDrawMessage;
              btn.style.cursor = 'not-allowed';
            }
          } catch (_) {}
        });
        drawGuardBoundRef.current = true;
      } catch (_) {}
    };
    // Attempt binding immediately and again on next tick for safety (Draw DOM may not be ready yet)
    bindDrawToolbarGuards();
    setTimeout(bindDrawToolbarGuards, 0);

    // Set up event listeners
    if (map.current && typeof map.current.on === 'function') {
      map.current.on('moveend', persistMapView);
      map.current.on('load', () => {
        console.log('Map loaded');
        setMapLoaded(true);
        persistMapView();
        // Immediate, non-animated focus for parcel params to avoid initial drift in some browsers
        try {
          const qs = typeof window !== 'undefined' ? window.location.search || '' : '';
          if (qs) {
            const params = new URLSearchParams(qs);
            const bboxStr = params.get('parcel_bbox');
            const ctrStr = params.get('parcel_center');
            if (bboxStr) {
              const parts = bboxStr.split(',').map(Number);
              if (parts.length === 4 && !parts.some((n) => Number.isNaN(n))) {
                const [w, s, e, n] = parts;
                const bounds = new mapboxgl.LngLatBounds([w, s], [e, n]);
                try { map.current.fitBounds(bounds, { padding: 50, maxZoom: 17, duration: 0 }); } catch (_) {}
                try { map.current.__yona_parcelFocused = true; } catch(_) {}
                try { map.current.__yona_initialViewStrategy = 'query'; } catch(_) {}
              }
            } else if (ctrStr) {
              const arr = ctrStr.split(',').map(Number);
              if (arr.length === 2 && !arr.some((n) => Number.isNaN(n))) {
                try {
                  const zoom = Math.max(15, (map.current.getZoom ? map.current.getZoom() : 15));
                  map.current.jumpTo({ center: arr, zoom, bearing: 0, pitch: (map.current.getPitch ? map.current.getPitch() : 0) });
                } catch (_) {}
                try { map.current.__yona_parcelFocused = true; } catch(_) {}
                try { map.current.__yona_initialViewStrategy = 'query'; } catch(_) {}
              }
            }
          }
        } catch (_) {}

        const ensureReferenceOverlay = (on) => {
          applyEsriReferenceOverlays(map.current, on, {
            transportationUrl: ESRI_REFERENCE_TRANSPORTATION,
            boundariesPlacesUrl: ESRI_REFERENCE_BOUNDARIES_PLACES
          });
        };
        // Add initially if enabled
        ensureReferenceOverlay(!!showReferenceLabels);
        // Expose toggle for UI wiring within this closure
        map.current.__yona_setReferenceOverlay = ensureReferenceOverlay;

        // Helper: enable/disable 3D view (terrain optional)
        const ensure3DView = (on) => {
          if (!map.current) return;
          try {
            if (on) {
              // Optional terrain if token available
              if (MAPBOX_ACCESS_TOKEN) {
                if (!map.current.getSource('mapbox-dem')) {
                  map.current.addSource('mapbox-dem', {
                    type: 'raster-dem',
                    url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
                    tileSize: 512,
                    maxzoom: 14
                  });
                }
                try { map.current.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 }); } catch (_) {}
              } else {
                // No token: skip terrain but still tilt view
                try { map.current.setTerrain && map.current.setTerrain(null); } catch (_) {}
              }
              try { map.current.easeTo({ pitch: 45, bearing: 0, duration: 500 }); } catch (_) {}
            } else {
              // Disable terrain and return to 2D pitch
              try { map.current.setTerrain && map.current.setTerrain(null); } catch (_) {}
              try { map.current.easeTo({ pitch: 0, bearing: 0, duration: 400 }); } catch (_) {}
              // Optionally remove DEM source to reduce background network/cpu
              try { if (map.current.getSource && map.current.getSource('mapbox-dem')) map.current.removeSource('mapbox-dem'); } catch (_) {}
            }
          } catch (_) {}
          // Touch only: allow tilt/rotate in 3D; re-harden pan/pinch in 2D
          try {
            const touchFlag = typeof map.current.__yona_touchMapGestures === 'boolean'
              ? map.current.__yona_touchMapGestures
              : touchMapGestures;
            syncMobileMapGesturesFor3D(map.current, !!on, { touchMapGestures: touchFlag });
          } catch (_) {}
        };
        // Ensure initial 2D state and expose setter
        ensure3DView(false);
        map.current.__yona_set3DView = (on) => {
          ensure3DView(!!on);
          try { setIs3D(!!on); } catch(_) {}
        };

        // Pre-configure Regrid layer click handler binding helper
        try {
          const onParcelClick = (e) => {
            try { if (map.current && map.current.__yona_isDrawing) return; } catch(_) {}
            const f = e && e.features && e.features[0];
            if (!f) return;
            if (!isTapNotDrag(map.current.__yona_parcelPointerDown, e && e.point)) return;
            // Selection mode vs info is decided by the shared dispatcher.
            const parcel = parcelFromFeature(f);
            if (parcel && dispatchParcelRef.current) dispatchParcelRef.current(parcel);
          };
          // Store for later attach/detach
          map.current.__yona_onParcelClick = onParcelClick;
        } catch (_) {}

        // Debug helpers
        try {
          if (debug) {
            // Expose for manual console inspection
            window.__yona_map = map.current;
            // Background layer below everything to avoid pure white
            if (!map.current.getLayer('yona-debug-bg')) {
              // Insert at the bottom
              map.current.addLayer({ id: 'yona-debug-bg', type: 'background', paint: { 'background-color': '#000' } }, undefined);
            }
            // Reduce raster fade to avoid long crossfades to non-existent tiles
            const rasterLayerIds = ['esri-satellite-layer', 'esri-topo-layer', 'esri-street-layer'];
            rasterLayerIds.forEach(id => {
              if (map.current.getLayer(id)) {
                try { map.current.setPaintProperty(id, 'raster-fade-duration', 0); } catch(_) {}
              }
            });
            // Visualize tile boundaries and log zoom
            try { map.current.showTileBoundaries = true; } catch(_) {}
            map.current.on('zoomend', () => {
              try { console.log('[map] zoom=', map.current.getZoom()); } catch(_) {}
            });
            // Log tile requests (best-effort)
            map.current.on('sourcedata', (e) => {
              const z = map.current && map.current.getZoom ? map.current.getZoom() : null;
              const tileId = e && e.tile && e.tile.tileID && e.tile.tileID.canonical;
              if (tileId) {
                console.log('[tile]', `z=${tileId.z} x=${tileId.x} y=${tileId.y}`, 'mapZoom=', z);
              }
            });
          }
        } catch (_) {}
        
        // Add 3D terrain if we have a Mapbox access token and haven't added it yet
        if (MAPBOX_ACCESS_TOKEN && !terrainAddedRef.current) {
          try {
            // Add the terrain DEM source on top of Esri satellite
            map.current.addSource('mapbox-dem', {
              'type': 'raster-dem',
              'url': 'mapbox://mapbox.mapbox-terrain-dem-v1',
              'tileSize': 512,
              'maxzoom': 14
            });
            
            // Set the terrain with exaggeration for dramatic effect
            map.current.setTerrain({ 
              'source': 'mapbox-dem', 
              'exaggeration': 1.5 
            });
            
            terrainAddedRef.current = true;
            console.log('3D terrain enabled with Esri satellite base');
          } catch (error) {
            console.warn('Failed to enable 3D terrain:', error);
          }
        }

        // Ensure required icons are available (fallbacks if missing)
        try {
          const ensureMarkerIcon = () => {
            if (map.current.hasImage('marker-15')) return;
            const size = 24;
            const canvas = document.createElement('canvas');
            canvas.width = size; canvas.height = size;
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, size, size);
            // outline
            ctx.beginPath();
            ctx.arc(size/2, size/2, size/2 - 1, 0, Math.PI*2);
            ctx.strokeStyle = 'rgba(255,255,255,1)';
            ctx.lineWidth = 2;
            ctx.stroke();
            // fill (blue)
            ctx.beginPath();
            ctx.arc(size/2, size/2, size/2 - 3, 0, Math.PI*2);
            ctx.fillStyle = 'rgba(0,123,255,1)';
            ctx.fill();
            const imgData = ctx.getImageData(0, 0, size, size);
            map.current.addImage('marker-15', { width: size, height: size, data: new Uint8Array(imgData.data) }, { pixelRatio: 2 });
          };

          const ensureStandIcon = async () => {
            if (map.current.hasImage('stand-icon')) return;
            // Try loading external white SVG into an HTMLImageElement
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              try { map.current.addImage('stand-icon', img, { pixelRatio: 2 }); } catch {}
            };
            img.onerror = () => {
              // Fallback: simple white triangle
              const size = 24;
              const canvas = document.createElement('canvas');
              canvas.width = size; canvas.height = size;
              const ctx = canvas.getContext('2d');
              ctx.clearRect(0, 0, size, size);
              ctx.fillStyle = 'rgba(255,255,255,1)';
              ctx.beginPath();
              ctx.moveTo(size/2, 3);
              ctx.lineTo(size-3, size-3);
              ctx.lineTo(3, size-3);
              ctx.closePath();
              ctx.fill();
              const imgData = ctx.getImageData(0, 0, size, size);
              map.current.addImage('stand-icon', { width: size, height: size, data: new Uint8Array(imgData.data) }, { pixelRatio: 2 });
            };
            img.src = '/icons/stand-white.svg';
          };

          const addSvgImage = (id, path) => {
            if (map.current.hasImage(id)) return;
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              try { map.current.addImage(id, img, { pixelRatio: 2 }); } catch {}
            };
            img.onerror = () => {};
            img.src = path;
          };

          const ensureWindWedgeImages = () => {
            // Create two canvas-based annular sector (45°) wedges centered at image center
            // One normal green, one glowing green for match
            const makeWedge = (id, fillStyle, glow) => {
              if (map.current.hasImage(id)) return;
              const size = 64;
              const canvas = document.createElement('canvas');
              canvas.width = size; canvas.height = size;
              const ctx = canvas.getContext('2d');
              ctx.clearRect(0, 0, size, size);
              const cx = size / 2; const cy = size / 2;
              const innerR = 14; // transparent hole radius
              const outerR = 28; // ring outer radius
              const startAngle = -Math.PI / 8; // -22.5° so that rotate 0 aligns to N
              const endAngle = Math.PI / 8;   // +22.5° total 45° wedge
              if (glow) {
                ctx.shadowColor = 'rgba(40, 167, 69, 0.85)';
                ctx.shadowBlur = 12;
              }
              ctx.fillStyle = fillStyle;
              ctx.beginPath();
              // Outer arc
              ctx.arc(cx, cy, outerR, startAngle, endAngle, false);
              // Line to inner arc end
              const ix2 = cx + innerR * Math.cos(endAngle);
              const iy2 = cy + innerR * Math.sin(endAngle);
              ctx.lineTo(ix2, iy2);
              // Inner arc back
              ctx.arc(cx, cy, innerR, endAngle, startAngle, true);
              ctx.closePath();
              ctx.fill();
              const imgData = ctx.getImageData(0, 0, size, size);
              map.current.addImage(id, { width: size, height: size, data: new Uint8Array(imgData.data) }, { pixelRatio: 2 });
            };
            makeWedge('wind-wedge-green', 'rgba(40,167,69,0.35)', false);
            makeWedge('wind-wedge-green-glow', 'rgba(40,167,69,0.85)', true);
          };

          const ensureWindArrowImages = () => {
            const makeArrow = (id, color) => {
              if (map.current.hasImage(id)) return;
              const size = 40;
              const canvas = document.createElement('canvas');
              canvas.width = size; canvas.height = size;
              const ctx = canvas.getContext('2d');
              ctx.clearRect(0, 0, size, size);
              const cx = size / 2; const cy = size / 2;
              const innerR = 16; // base near ring
              const outerR = 26; // arrow head near outer ring
              // Draw an isosceles triangle pointing up (we'll rotate per direction)
              ctx.fillStyle = color;
              ctx.beginPath();
              // base left
              const bw = 4;
              ctx.moveTo(cx - bw, cy - innerR);
              // base right
              ctx.lineTo(cx + bw, cy - innerR);
              // tip
              ctx.lineTo(cx, cy - outerR);
              ctx.closePath();
              ctx.fill();
              const imgData = ctx.getImageData(0, 0, size, size);
              map.current.addImage(id, { width: size, height: size, data: new Uint8Array(imgData.data) }, { pixelRatio: 2 });
            };
            makeArrow('wind-arrow-grey', 'rgba(160,160,160,0.8)');
            makeArrow('wind-arrow-green', 'rgba(40,167,69,0.95)');
          };

          // Only attempt if DOM canvas is available
          if (typeof document !== 'undefined') {
            ensureMarkerIcon();
            ensureStandIcon();
            // Load white marker-type icons
            addSvgImage('marker-feeder', '/icons/marker-feeder-white.svg');
            addSvgImage('marker-mineral', '/icons/marker-mineral-white.svg');
            addSvgImage('marker-camera', '/icons/marker-camera-white.svg');
            addSvgImage('marker-deer', '/icons/marker-deer-white.svg');
            addSvgImage('marker-gate', '/icons/marker-gate-white.svg');
            ensureWindWedgeImages();
            ensureWindArrowImages();
          }
        } catch (e) {
          console.warn('Failed to ensure icons:', e);
        }
        
        // Add custom control: one waypoint drop (stand and feed are toggles in the modal)
        try {
          const startWaypoint = onStartDropWaypoint || onStartDropMarker || onStartPlaceStand;
          const Handlers = { startWaypoint };
          class MarkerStandControl {
            onAdd(m) {
              this._map = m;
              this._container = document.createElement('div');
              this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';

              const btnWaypoint = document.createElement('button');
              btnWaypoint.type = 'button';
              btnWaypoint.className = 'mapboxgl-ctrl-icon yona-ctrl-btn';
              btnWaypoint.title = 'Drop waypoint';
              btnWaypoint.setAttribute('aria-label', 'Drop waypoint');
              btnWaypoint.setAttribute('aria-pressed', 'false');
              btnWaypoint.dataset.yonaTool = 'point';
              const waypointImgEl = document.createElement('img');
              waypointImgEl.src = '/icons/marker-bw.svg';
              waypointImgEl.alt = 'Drop waypoint';
              waypointImgEl.style.width = '20px';
              waypointImgEl.style.height = '20px';
              btnWaypoint.appendChild(waypointImgEl);
              btnWaypoint.addEventListener('click', (e) => {
                e.preventDefault();
                if (!canDrawRef.current) { try { alert(blockedDrawMessage); } catch(_) {} return; }
                if (typeof Handlers.startWaypoint === 'function') Handlers.startWaypoint();
              });

              this._container.appendChild(btnWaypoint);
              this._btnMarker = btnWaypoint;
              this._btnStand = null;
              try {
                m.__yona_markerStandCtrl = this;
                m.__yona_syncPlacementToolChrome = () => {
                  let drawMode = 'simple_select';
                  try {
                    drawMode = (draw.current && typeof draw.current.getMode === 'function')
                      ? draw.current.getMode()
                      : 'simple_select';
                  } catch (_) {}
                  applyPlacementToolChrome({
                    waypointBtn: btnWaypoint,
                    creationContext: m.__yona_creationContext || null,
                    drawMode,
                  });
                };
              } catch (_) {}
              // Do not disable the buttons; the click handler will guard.
              return this._container;
            }
            onRemove() {
              if (this._container && this._container.parentNode) {
                this._container.parentNode.removeChild(this._container);
              }
              this._map = undefined;
            }
          }

          customCtrlRef.current = new MarkerStandControl();
          if (map.current && typeof map.current.addControl === 'function') {
            // Add to top-right to sit with Navigation and Draw controls
            map.current.addControl(customCtrlRef.current, 'top-right');
          }
        } catch (e) {
          console.warn('Failed to add custom control:', e);
        }

        // Add 2D/3D view toggle control (placed above Parcels control)
        try {
          class ViewModeControl {
            constructor() { this._on = false; }
            onAdd(m) {
              this._map = m;
              this._container = document.createElement('div');
              this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
              const btn = document.createElement('button');
              btn.type = 'button';
              btn.className = 'mapboxgl-ctrl-icon yona-ctrl-btn';
              btn.style.fontWeight = '600';
              btn.style.fontSize = '12px';
              btn.style.lineHeight = '20px';
              btn.style.letterSpacing = '0.2px';
              btn.style.padding = '0 6px';
              btn.style.display = 'flex';
              btn.style.alignItems = 'center';
              btn.style.justifyContent = 'center';
              const apply = () => {
                btn.setAttribute('aria-pressed', this._on ? 'true' : 'false');
                // Show target mode text: in 2D show "3D", in 3D show "2D"
                const target = this._on ? '2D' : '3D';
                btn.textContent = target;
                btn.setAttribute('aria-label', `Switch to ${target}`);
                btn.title = `Switch to ${target}`;
              };
              btn.addEventListener('click', () => {
                this._on = !this._on;
                try { m && m.__yona_set3DView && m.__yona_set3DView(this._on); } catch(_) {}
                apply();
              });
              this._container.appendChild(btn);
              this._btn = btn;
              apply();
              return this._container;
            }
            onRemove() {
              if (this._container && this._container.parentNode) {
                this._container.parentNode.removeChild(this._container);
              }
              this._map = undefined;
            }
            setOn(on) {
              this._on = !!on;
              if (this._btn) {
                this._btn.setAttribute('aria-pressed', this._on ? 'true' : 'false');
                const target = this._on ? '2D' : '3D';
                this._btn.textContent = target;
                this._btn.setAttribute('aria-label', `Switch to ${target}`);
                this._btn.title = `Switch to ${target}`;
              }
            }
          }
          const viewCtrl = new ViewModeControl();
          map.current.addControl(viewCtrl, 'top-right');
          // Keep control in sync with state changes
          map.current.__yona_viewCtrl = viewCtrl;
        } catch (e) {
          console.warn('Failed to add 3D view control:', e);
        }

        // Add Parcels control with popover (toggle + selection + create boundary)
        if (showParcelsControl) try {
          class ParcelsControl {
            constructor(getParcelsOn, toggleParcels, getSelectionOn, getHasSelection, getIsBusy, onCreateOrSave, getLabelsOn, toggleLabels, getPublicLandsOn, togglePublicLands, getSoilOn, toggleSoil, getSoilMode, setSoilMode) {
              this.getParcelsOn = getParcelsOn;
              this.toggleParcels = toggleParcels;
              this.getSelectionOn = getSelectionOn;
              this.getHasSelection = getHasSelection;
              this.getIsBusy = getIsBusy;
              this.onCreateOrSave = onCreateOrSave;
              this.getLabelsOn = getLabelsOn;
              this.toggleLabels = toggleLabels;
              this.getPublicLandsOn = getPublicLandsOn;
              this.togglePublicLands = togglePublicLands;
              this.getSoilOn = getSoilOn;
              this.toggleSoil = toggleSoil;
              this.getSoilMode = getSoilMode;
              this.setSoilMode = setSoilMode;
              this._open = false;
            }
            onAdd(m) {
              this._map = m;
              this._container = document.createElement('div');
              this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
              this._container.style.position = 'relative';
              this._container.style.overflow = 'visible';
              // Ensure all ancestor wrappers allow overflow so the panel isn't clipped
              try {
                let p = this._container.parentNode;
                let guard = 0;
                while (p && guard < 5) {
                  try { p.style && (p.style.overflow = 'visible'); } catch(_) {}
                  if ((p.className || '').toString().indexOf('mapboxgl-ctrl-top-right') >= 0) break;
                  p = p.parentNode;
                  guard += 1;
                }
              } catch(_) {}
              const btn = document.createElement('button');
              btn.type = 'button';
              btn.className = 'mapboxgl-ctrl-icon yona-ctrl-btn';
              btn.title = 'Parcels';
              btn.setAttribute('aria-label', 'Parcels');
              btn.setAttribute('aria-pressed', this.getParcelsOn() ? 'true' : 'false');
              // Simple glyph (parcel grid)
              btn.innerHTML = `
                <svg width="20" height="20" viewBox="0 0 20 20" style="display:block;margin:auto">
                  <rect x="2" y="2" width="6" height="6" fill="none" stroke="currentColor" stroke-width="2"/>
                  <rect x="12" y="2" width="6" height="6" fill="none" stroke="currentColor" stroke-width="2"/>
                  <rect x="2" y="12" width="6" height="6" fill="none" stroke="currentColor" stroke-width="2"/>
                  <rect x="12" y="12" width="6" height="6" fill="none" stroke="currentColor" stroke-width="2"/>
                </svg>
              `;
              const updatePressed = () => {
                const active = this.getParcelsOn();
                btn.setAttribute('aria-pressed', active ? 'true' : 'false');
              };
              btn.addEventListener('click', () => {
                try {
                  this._open = !this._open;
                  // Re-host first: the breakpoint may have changed since last open.
                  if (typeof this._positionParcelsPanel === 'function') {
                    this._positionParcelsPanel();
                  }
                  if (typeof applyOpen === 'function') applyOpen();
                } catch(_) {}
              });
              this._container.appendChild(btn);
              this._btn = btn;
              // Layers panel. Geometry, colors and the slide animation live in
              // mapChromeFieldEdition.css (--drawer on desktop, --sheet on phone);
              // inline styles here would outrank them.
              const panel = document.createElement('div');
              panel.className = 'yona-parcels-panel';
              panel.setAttribute('role', 'dialog');
              panel.setAttribute('aria-label', 'Map layers');

              const handle = document.createElement('div');
              handle.className = 'yona-parcels-panel__handle';
              panel.appendChild(handle);

              const header = document.createElement('div');
              header.className = 'yona-parcels-panel__header';
              const headerTitle = document.createElement('span');
              headerTitle.className = 'yona-parcels-panel__title';
              headerTitle.textContent = 'Map Layers';
              const closeBtn = document.createElement('button');
              closeBtn.type = 'button';
              closeBtn.className = 'yona-parcels-panel__close';
              closeBtn.setAttribute('aria-label', 'Close layers');
              closeBtn.textContent = '✕';
              closeBtn.addEventListener('click', () => {
                this._open = false;
                if (typeof applyOpen === 'function') applyOpen();
                updatePressed();
              });
              header.appendChild(headerTitle);
              header.appendChild(closeBtn);
              panel.appendChild(header);

              const isSheetLayout = () => {
                try {
                  return !!(window.matchMedia && window.matchMedia(PARCELS_SHEET_MEDIA_QUERY).matches);
                } catch (_) {
                  return false;
                }
              };

              /** Positioned ancestor for the desktop drawer, so it slides in over
               *  the map area rather than being sized by the ~40px ctrl rail. */
              const resolveDrawerHost = () => {
                const el = mapContainer.current;
                if (el) {
                  const mapArea = el.closest && el.closest('.map-container');
                  if (mapArea) return mapArea;
                  if (el.parentElement) return el.parentElement;
                }
                return this._container;
              };

              /**
               * Re-host the panel out of the Mapbox ctrl rail: that container sizes
               * and clips it (it collapsed the sheet into a one-word column).
               * Phone → <body> bottom sheet; desktop → map-area right drawer.
               */
              const syncPanelHost = () => {
                const sheet = isSheetLayout();
                panel.classList.toggle('yona-parcels-panel--sheet', sheet);
                panel.classList.toggle('yona-parcels-panel--drawer', !sheet);
                const host = sheet ? document.body : resolveDrawerHost();
                if (host && panel.parentNode !== host) {
                  host.appendChild(panel);
                }
                return sheet;
              };

              // CSS owns placement for both variants; only the host and the
              // anchor marker change here.
              const positionPanel = () => {
                panel.dataset.anchor = syncPanelHost() ? 'sheet' : 'drawer';
              };

              const applyOpen = () => {
                // Slide in/out is CSS-driven off the .open class.
                panel.classList.toggle('open', !!this._open);
              };

              positionPanel();
              applyOpen();
              this._positionParcelsPanel = positionPanel;

              const onOutsidePointerDown = (e) => {
                if (!this._open) return;
                if (this._panel && this._panel.contains(e.target)) return;
                if (this._btn && this._btn.contains(e.target)) return;
                this._open = false;
                if (typeof applyOpen === 'function') applyOpen();
                updatePressed();
              };
              document.addEventListener('pointerdown', onOutsidePointerDown);
              this._onOutsidePointerDown = onOutsidePointerDown;

              // Breakpoint changes must move the panel between rail and <body>.
              const onViewportChange = () => {
                positionPanel();
                applyOpen();
              };
              window.addEventListener('resize', onViewportChange);
              this._onViewportChange = onViewportChange;
              // Helper to build a slider toggle row
              const buildSliderRow = (labelText, isOnFn, onToggleFn) => {
                const row = document.createElement('div');
                row.style.display = 'flex';
                row.style.alignItems = 'center';
                row.style.justifyContent = 'space-between';
                row.style.gap = '12px';
                const text = document.createElement('span');
                text.textContent = labelText;
                text.style.whiteSpace = 'normal';
                text.style.userSelect = 'none';
                const slider = document.createElement('div');
                slider.style.position = 'relative';
                slider.style.width = '42px';
                slider.style.height = '22px';
                slider.style.borderRadius = '999px';
                slider.style.background = isOnFn() ? '#22c55e' : '#555';
                slider.style.cursor = 'pointer';
                slider.style.flex = '0 0 auto';
                const knob = document.createElement('div');
                knob.style.position = 'absolute';
                knob.style.top = '2px';
                knob.style.left = isOnFn() ? '22px' : '2px';
                knob.style.width = '18px';
                knob.style.height = '18px';
                knob.style.borderRadius = '50%';
                knob.style.background = '#fff';
                knob.style.boxShadow = '0 1px 2px rgba(0,0,0,0.3)';
                knob.style.transition = 'left 160ms ease';
                slider.appendChild(knob);
                const applyState = () => {
                  const on = !!isOnFn();
                  slider.style.background = on ? '#22c55e' : '#555';
                  knob.style.left = on ? '22px' : '2px';
                };
                slider.addEventListener('click', () => {
                  try {
                    const target = !isOnFn();
                    onToggleFn && onToggleFn(target);
                    // Immediate visual feedback without waiting for React state
                    slider.style.background = target ? '#22c55e' : '#555';
                    knob.style.left = target ? '22px' : '2px';
                    updatePressed();
                  } catch(_) {}
                });
                row.appendChild(text);
                row.appendChild(slider);
                return { row, applyState, slider };
              };
              // Labels (reference overlay) toggle row
              const labelsRow = buildSliderRow('Map labels', () => this.getLabelsOn(), (next) => this.toggleLabels(next));
              // Parcels toggle row
              const parcelsRow = buildSliderRow('Parcels', () => this.getParcelsOn(), (next) => this.toggleParcels(next));
              // Public lands toggle row
              const publicLandsRow = buildSliderRow('Public Lands', () => this.getPublicLandsOn(), (next) => this.togglePublicLands(next));
              // Soil toggle row
              const soilRow = buildSliderRow('Soil', () => this.getSoilOn(), (next) => this.toggleSoil(next));
              // Create/Save + Exit row
              const actionRow = document.createElement('div');
              actionRow.style.display = 'flex';
              actionRow.style.alignItems = 'center';
              actionRow.style.gap = '8px';
              const createBtn = document.createElement('button');
              createBtn.type = 'button';
              const computeBtnText = () => {
                const parcelsOn = !!this.getParcelsOn();
                const selecting = !!this.getSelectionOn();
                const hasSel = !!this.getHasSelection();
                const busy = !!this.getIsBusy();
                if (!parcelsOn) return 'Create boundary';
                if (busy) return 'Saving…';
                if (selecting && hasSel) return 'Save boundary';
                return 'Create boundary';
              };
              createBtn.textContent = computeBtnText();
              createBtn.style.background = '#fff';
              createBtn.style.color = '#000';
              createBtn.style.border = 'none';
              createBtn.style.borderRadius = '4px';
              createBtn.style.padding = '4px 8px';
              createBtn.style.display = 'inline-block';
              createBtn.style.flex = '1 1 auto';
              createBtn.addEventListener('click', () => {
                try {
                  const parcelsOn = !!this.getParcelsOn();
                  const selecting = !!this.getSelectionOn();
                  const hasSel = !!this.getHasSelection();
                  const busy = !!this.getIsBusy();
                  try { console.log('[parcel][ui] action button click', { parcelsOn, selecting, hasSel, busy }); } catch(_) {}
                  // Ensure parcels ON
                  if (!this.getParcelsOn()) this.toggleParcels();
                  // Delegate to parent to either begin selection or save
                  this.onCreateOrSave && this.onCreateOrSave();
                  // Keep panel open; parent will update button state/text via update()
                } catch(_) {}
              });
              const exitBtn = document.createElement('button');
              exitBtn.type = 'button';
              exitBtn.setAttribute('aria-label', 'Exit boundary selection');
              exitBtn.title = 'Exit';
              exitBtn.innerHTML = '✕';
              exitBtn.style.background = 'transparent';
              exitBtn.style.color = '#fff';
              exitBtn.style.border = '1px solid rgba(255,255,255,0.4)';
              exitBtn.style.borderRadius = '4px';
              exitBtn.style.padding = '2px 6px';
              exitBtn.style.display = this.getSelectionOn() ? 'inline-block' : 'none';
              exitBtn.addEventListener('click', () => {
                try {
                  typeof onExitParcelSelection === 'function' && onExitParcelSelection();
                } catch(_) {}
              });
              actionRow.appendChild(createBtn);
              actionRow.appendChild(exitBtn);
              panel.appendChild(labelsRow.row);
              panel.appendChild(parcelsRow.row);
              panel.appendChild(publicLandsRow.row);
              panel.appendChild(soilRow.row);

              // Soil mode toggle (Drainage / Texture)
              const soilModeWrap = document.createElement('div');
              soilModeWrap.style.display = this.getSoilOn() ? 'flex' : 'none';
              soilModeWrap.style.flexDirection = 'column';
              soilModeWrap.style.gap = '6px';
              soilModeWrap.style.alignItems = 'stretch';
              soilModeWrap.style.flexWrap = 'wrap';
              soilModeWrap.style.width = '100%';
              const soilModeLabel = document.createElement('span');
              soilModeLabel.textContent = '';
              soilModeLabel.style.fontSize = '12px';
              soilModeLabel.style.color = '#ccc';
              // Colors live in mapChromeFieldEdition.css so the inactive label stays
              // readable ink on paper; only layout is set inline here.
              const makeModeBtn = (label, value) => {
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.textContent = label;
                btn.className = 'yona-parcels-panel__mode-btn';
                btn.style.borderRadius = '4px';
                btn.style.padding = '4px 10px';
                btn.style.cursor = 'pointer';
                btn.style.flex = '1 1 45%';
                btn.style.minWidth = '110px';
                btn.style.textAlign = 'center';
                btn.addEventListener('click', () => {
                  try { this.setSoilMode(value); } catch (_) {}
                  try { applySoilModeState(); } catch (_) {}
                });
                return btn;
              };
              const drainageBtn = makeModeBtn('Drainage', 'drainage');
              const textureBtn = makeModeBtn('Type', 'texture');
              const applySoilModeState = () => {
                const mode = this.getSoilMode();
                const activeClass = 'yona-parcels-panel__mode-btn--active';
                drainageBtn.classList.toggle(activeClass, mode === 'drainage');
                drainageBtn.setAttribute('aria-pressed', mode === 'drainage' ? 'true' : 'false');
                textureBtn.classList.toggle(activeClass, mode === 'texture');
                textureBtn.setAttribute('aria-pressed', mode === 'texture' ? 'true' : 'false');
                soilModeWrap.style.display = this.getSoilOn() ? 'flex' : 'none';
              };
              soilModeWrap.appendChild(soilModeLabel);
              const btnRow = document.createElement('div');
              btnRow.style.display = 'flex';
              btnRow.style.gap = '6px';
              btnRow.style.flexWrap = 'wrap';
              btnRow.style.width = '100%';
              btnRow.appendChild(drainageBtn);
              btnRow.appendChild(textureBtn);
              soilModeWrap.appendChild(btnRow);
              panel.appendChild(soilModeWrap);

              // Soil legend
              const soilLegend = document.createElement('div');
              soilLegend.style.display = this.getSoilOn() ? 'flex' : 'none';
              soilLegend.style.flexDirection = 'column';
              soilLegend.style.gap = '4px';
              soilLegend.style.marginTop = '6px';
              const buildSoilLegendItems = (items) => {
                const wrap = document.createElement('div');
                wrap.style.display = 'flex';
                wrap.style.flexDirection = 'column';
                wrap.style.gap = '4px';
                items.forEach((item) => {
                  const entry = document.createElement('div');
                  entry.style.display = 'flex';
                  entry.style.alignItems = 'center';
                  entry.style.gap = '8px';
                  const swatch = document.createElement('span');
                  swatch.style.display = 'inline-block';
                  swatch.style.width = '12px';
                  swatch.style.height = '12px';
                  swatch.style.borderRadius = '2px';
                  swatch.style.background = item.color;
                  const label = document.createElement('span');
                  label.style.fontSize = '12px';
                  label.textContent = item.label;
                  entry.appendChild(swatch);
                  entry.appendChild(label);
                  wrap.appendChild(entry);
                });
                return wrap;
              };
              const soilLegendDrainage = buildSoilLegendItems(SOIL_LEGEND_DRAINAGE);
              const soilLegendTexture = buildSoilLegendItems(SOIL_LEGEND_TEXTURE);
              soilLegendDrainage.style.display = this.getSoilMode() === 'drainage' ? 'flex' : 'none';
              soilLegendTexture.style.display = this.getSoilMode() === 'texture' ? 'flex' : 'none';
              soilLegend.appendChild(soilLegendDrainage);
              soilLegend.appendChild(soilLegendTexture);
              panel.appendChild(soilLegend);

              const legend = document.createElement('div');
              legend.style.display = this.getPublicLandsOn() ? 'flex' : 'none';
              legend.style.flexDirection = 'column';
              legend.style.gap = '4px';
              legend.style.marginTop = '6px';
              legend.style.paddingTop = '6px';
              legend.style.borderTop = '1px solid rgba(255,255,255,0.2)';
              PUBLIC_LANDS_LEGEND.forEach((item) => {
                const entry = document.createElement('div');
                entry.style.display = 'flex';
                entry.style.alignItems = 'center';
                entry.style.gap = '8px';
                const swatch = document.createElement('span');
                swatch.style.display = 'inline-block';
                swatch.style.width = '12px';
                swatch.style.height = '12px';
                swatch.style.borderRadius = '50%';
                swatch.style.background = item.color;
                const label = document.createElement('span');
                label.style.fontSize = '12px';
                label.textContent = item.label;
                entry.appendChild(swatch);
                entry.appendChild(label);
                legend.appendChild(entry);
              });
              panel.appendChild(legend);
              panel.appendChild(actionRow);

              const sourceNote = document.createElement('p');
              sourceNote.className = 'yona-parcels-panel__source';
              sourceNote.setAttribute('data-testid', 'map-layers-source');
              sourceNote.textContent = 'PARCELS · LANDPARCELS.US / SOILS · SSURGO';
              panel.appendChild(sourceNote);

              this._panel = panel;
              // Host depends on breakpoint (rail on desktop, <body> for the sheet).
              syncPanelHost();
              this._labelsSliderApply = labelsRow.applyState;
              this._parcelsSliderApply = parcelsRow.applyState;
              this._publicLandsSliderApply = publicLandsRow.applyState;
              this._publicLandsLegend = legend;
              this._soilSliderApply = soilRow.applyState;
              this._soilLegend = soilLegend;
              this._soilModeWrap = soilModeWrap;
              this._soilLegendDrainage = soilLegendDrainage;
              this._soilLegendTexture = soilLegendTexture;
              this._applySoilModeState = applySoilModeState;
              this._createBtn = createBtn;
              this._exitBtn = exitBtn;
              this._applyOpen = applyOpen;
              this._actionRow = actionRow;
              // Initialize visual state
              updatePressed();
              try { this._soilSliderApply && this._soilSliderApply(); } catch (_) {}
              try { this._applySoilModeState && this._applySoilModeState(); } catch (_) {}
              applyOpen();
              return this._container;
            }
            onRemove() {
              if (this._onOutsidePointerDown) {
                document.removeEventListener('pointerdown', this._onOutsidePointerDown);
                this._onOutsidePointerDown = null;
              }
              if (this._onViewportChange) {
                window.removeEventListener('resize', this._onViewportChange);
                this._onViewportChange = null;
              }
              // Body-hosted sheet lives outside _container, so remove it explicitly.
              if (this._panel && this._panel.parentNode) {
                this._panel.parentNode.removeChild(this._panel);
              }
              if (this._container && this._container.parentNode) {
                this._container.parentNode.removeChild(this._container);
              }
              this._map = undefined;
            }
            update() {
              if (!this._btn) return;
              const active = this.getParcelsOn();
              this._btn.setAttribute('aria-pressed', active ? 'true' : 'false');
              try {
                if (this._panel) {
                  // Update slider visuals
                  this._labelsSliderApply && this._labelsSliderApply();
                  this._parcelsSliderApply && this._parcelsSliderApply();
                  this._publicLandsSliderApply && this._publicLandsSliderApply();
                  this._soilSliderApply && this._soilSliderApply();
                  if (this._publicLandsLegend) {
                    this._publicLandsLegend.style.display = this.getPublicLandsOn() ? 'flex' : 'none';
                  }
                  if (this._soilLegend) {
                    this._soilLegend.style.display = this.getSoilOn() ? 'flex' : 'none';
                  }
                  if (this._soilModeWrap) {
                    this._soilModeWrap.style.display = this.getSoilOn() ? 'flex' : 'none';
                  }
                  if (this._soilLegendDrainage && this._soilLegendTexture && this._applySoilModeState) {
                    const mode = this.getSoilMode();
                    this._soilLegendDrainage.style.display = mode === 'drainage' ? 'flex' : 'none';
                    this._soilLegendTexture.style.display = mode === 'texture' ? 'flex' : 'none';
                    this._applySoilModeState();
                  }
                  // Update create button label and disabled state; show only when parcels is on
                  if (this._actionRow) {
                    const parcelsOn = !!this.getParcelsOn();
                    this._actionRow.style.display = parcelsOn ? 'flex' : 'none';
                    if (this._createBtn) {
                      const selecting = !!this.getSelectionOn();
                      const hasSel = !!this.getHasSelection();
                      const busy = !!this.getIsBusy();
                      this._createBtn.textContent = parcelsOn ? (busy ? 'Saving…' : (selecting && hasSel ? 'Save boundary' : 'Create boundary')) : 'Create boundary';
                      this._createBtn.disabled = busy || !parcelsOn;
                      this._createBtn.style.opacity = busy ? '0.7' : '1';
                      this._createBtn.style.cursor = busy ? 'default' : 'pointer';
                    }
                    if (this._exitBtn) {
                      this._exitBtn.style.display = parcelsOn && this.getSelectionOn() ? 'inline-block' : 'none';
                    }
                  }
                }
              } catch (_) {}
            }
          }
          parcelsCtrlRef.current = new ParcelsControl(
            () => !!showRegridParcels,
            (next) => requestParcelsToggleRef.current && requestParcelsToggleRef.current(next),
            () => !!parcelSelectionEnabled,
            () => !!hasParcelSelection,
            () => !!isParcelCreateBusy,
            () => { try { typeof onRequestParcelBoundaryCreate === 'function' && onRequestParcelBoundaryCreate(); } catch(_) {} },
            () => !!showReferenceLabels,
            (next) => {
              const currentOn = parcelsCtrlRef.current && typeof parcelsCtrlRef.current.getLabelsOn === 'function'
                ? !!parcelsCtrlRef.current.getLabelsOn()
                : !!showReferenceLabels;
              const target = (typeof next === 'boolean') ? !!next : !currentOn;
              try { map.current && map.current.__yona_setReferenceOverlay && map.current.__yona_setReferenceOverlay(target); } catch(_) {}
              setShowReferenceLabels(target);
              try { parcelsCtrlRef.current && parcelsCtrlRef.current.update && parcelsCtrlRef.current.update(); } catch(_) {}
            },
            () => !!showPublicLands,
            (next) => {
              setShowPublicLands((prev) => {
                if (typeof next === 'boolean') return !!next;
                return !prev;
              });
            },
            () => !!showSoilRef.current,
            (next) => setShowSoil((prev) => (typeof next === 'boolean' ? !!next : !prev)),
            () => soilColorModeRef.current,
            (mode) => setSoilColorMode(mode === 'texture' ? 'texture' : 'drainage')
          );
          if (map.current && typeof map.current.addControl === 'function') {
            map.current.addControl(parcelsCtrlRef.current, 'top-right');
            setParcelsCtrlAdded(true);
          }
        } catch (e) {
          console.warn('Failed to add Parcels control:', e);
        }

        // (3D view control added earlier to appear above Parcels control)

        if (onMapReady) onMapReady(map.current, draw.current);
      });

      // Wait for map to be fully ready (idle event)
      map.current.on('idle', () => {
        console.log('Map idle - ready for features');
        setMapReady(true);
      });
    }

    // Drawing events (guarded: block creation/update for non-presidents on club maps via canDrawRef)
    if (map.current && typeof map.current.on === 'function') {
      map.current.on('draw.create', (e) => {
        if (!canDrawRef.current) {
          try { draw.current && e.features && e.features.forEach(f => draw.current.delete(f.id)); } catch(_) {}
          try { alert(blockedDrawMessage); } catch(_) {}
          return;
        }
        if (typeof onDrawCreateRef.current === 'function') onDrawCreateRef.current(e);
      });
      map.current.on('draw.update', (e) => {
        if (!canDrawRef.current) return;
        if (typeof onDrawUpdateRef.current === 'function') onDrawUpdateRef.current(e);
      });
      map.current.on('draw.render', (e) => {
        if (!canDrawRef.current) return;
        if (typeof onDrawUpdateRef.current === 'function') onDrawUpdateRef.current(e);
      });
      // When dragging a point (simple_select), also surface updates
      map.current.on('draw.selectionchange', (e) => {
        if (!canDrawRef.current) return;
        if (typeof onDrawUpdateRef.current === 'function') onDrawUpdateRef.current(e);
      });
      map.current.on('draw.modechange', (e) => {
        const mode = e && e.mode;
        try { if (typeof onDrawModeChangeRef.current === 'function') onDrawModeChangeRef.current(mode); } catch(_) {}
        // Treat actual draw modes as drawing; allow clicks in simple_select
        const isDrawing = Boolean(mode && (mode.indexOf('draw_') === 0 || mode === 'direct_select'));
        try { map.current.__yona_isDrawing = isDrawing; } catch(_) {}
        try { map.current.__yona_syncPlacementToolChrome && map.current.__yona_syncPlacementToolChrome(); } catch(_) {}
        // Option D: toggle doubleClickZoom during draw to prevent accidental zooms
        try {
          if (isDrawing) {
            map.current.doubleClickZoom && map.current.doubleClickZoom.disable();
          } else {
            map.current.doubleClickZoom && map.current.doubleClickZoom.enable();
          }
        } catch(_) {}
      });
      map.current.on('draw.delete', (e) => {
        if (typeof onDrawDeleteRef.current === 'function') onDrawDeleteRef.current(e);
      });
    }

    // Feature click events (mouse); skip if we just handled the same tap via touch
    const skipClickIfRecentlyHandledByTouch = () => {
      if (Date.now() - lastTouchHandledAtRef.current < 400) return true;
      return false;
    };
    // While a parcel mode is armed the parcel underneath owns the tap, so a
    // saved boundary cannot swallow it the way it does in normal map mode.
    // Both Identify and lot-selection are such modes.
    const parcelModeOwnsTap = () => {
      try {
        return !!(map.current && (map.current.__yona_parcelIdentify || map.current.__yona_parcelSelecting));
      } catch (_) { return false; }
    };
    const handleFeatureClickFromEvent = (e) => {
      e.preventDefault();
      if (map.current && map.current.__yona_isDrawing) return;
      if (parcelModeOwnsTap()) return;
      const feature = e.features && e.features[0];
      if (feature) onFeatureClick(feature);
    };
    if (onFeatureClick && map.current && typeof map.current.on === 'function') {
      FEATURE_CLICK_LAYERS.forEach((layerId) => {
        map.current.on('click', layerId, (e) => {
          if (skipClickIfRecentlyHandledByTouch()) return;
          handleFeatureClickFromEvent(e);
        });
      });

      // Touch path: map.on('click') often doesn't fire on mobile; use tap detection + queryRenderedFeatures
      const canvas = map.current.getCanvas();
      if (canvas) {
        const TAP_MOVE_THRESHOLD_PX_SQ = 225; // 15px
        const TAP_MAX_MS = 500;
        const handleTouchStart = (e) => {
          if (e.touches.length !== 1) return;
          touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: Date.now() };
        };
        const handleTouchEnd = (e) => {
          if (!e.changedTouches || !e.changedTouches[0]) return;
          const start = touchStartRef.current;
          touchStartRef.current = null;
          if (!start) return;
          const end = e.changedTouches[0];
          const dx = end.clientX - start.x;
          const dy = end.clientY - start.y;
          const dt = Date.now() - start.t;
          if (dx * dx + dy * dy > TAP_MOVE_THRESHOLD_PX_SQ || dt > TAP_MAX_MS) return;
          if (map.current && map.current.__yona_isDrawing) return;
          if (parcelModeOwnsTap()) return;
          const rect = canvas.getBoundingClientRect();
          const point = [end.clientX - rect.left, end.clientY - rect.top];
          let features = [];
          try {
            features = map.current.queryRenderedFeatures(point, { layers: FEATURE_CLICK_LAYERS }) || [];
          } catch (_) {}
          const feature = features[0];
          if (!feature) return;
          e.preventDefault();
          lastTouchHandledAtRef.current = Date.now();
          onFeatureClick(feature);
        };
        canvas.addEventListener('touchstart', handleTouchStart, { passive: true });
        canvas.addEventListener('touchend', handleTouchEnd, { passive: false });
        featureTouchHandlersRef.current = { canvas, handleTouchStart, handleTouchEnd };
      }
    }

    // Map move events
    if (onMapMove && map.current && typeof map.current.on === 'function') {
      map.current.on('moveend', () => {
        if (moveEndTimer.current) clearTimeout(moveEndTimer.current);
        moveEndTimer.current = setTimeout(() => {
          onMapMove({
            center: map.current.getCenter(),
            zoom: map.current.getZoom(),
            bounds: map.current.getBounds()
          });
        }, 100);
      });
    }

    // Notify that map is ready
    if (onMapReady) {
      onMapReady(map.current);
    }

    // Cleanup function
    return () => {
      console.log('Cleaning up map instance');
      initializedRef.current = false;
      
      if (moveEndTimer.current) clearTimeout(moveEndTimer.current);
      try { unbindDrawDragPanRecovery && unbindDrawDragPanRecovery(); } catch (_) {}
      
      // Remove drawing control first
      if (draw.current && map.current) {
        try {
          if (typeof map.current.removeControl === 'function') {
            map.current.removeControl(draw.current);
          }
        } catch (error) {
          console.warn('Error removing draw control:', error);
        }
        draw.current = null;
      }
      
      // Remove map (and feature touch listeners via canvas teardown)
      if (map.current) {
        try {
          const h = featureTouchHandlersRef.current;
          if (h && h.canvas && h.handleTouchStart && h.handleTouchEnd) {
            h.canvas.removeEventListener('touchstart', h.handleTouchStart);
            h.canvas.removeEventListener('touchend', h.handleTouchEnd);
            featureTouchHandlersRef.current = null;
          }
          try { map.current.off && map.current.off('moveend', persistMapView); } catch (_) {}
          if (typeof map.current.remove === 'function') {
            map.current.remove();
          }
        } catch (error) {
          console.warn('Error removing map:', error);
        }
        map.current = null;
      }
      // Remove custom control
      try {
        if (customCtrlRef.current && map.current && typeof map.current.removeControl === 'function') {
          map.current.removeControl(customCtrlRef.current);
        }
      } catch (e) {
        console.warn('Error removing custom control:', e);
      }
      
      // Reset terrain flag
      terrainAddedRef.current = false;
      
      // Reset state
      setMapLoaded(false);
      setMapReady(false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - initialize only once

  // Handle drawing mode changes - MOVED OUTSIDE INITIALIZATION
  useEffect(() => {
    if (draw.current && map.current && typeof draw.current.changeMode === 'function') {
      // Change the drawing mode based on the prop
      switch (drawingMode) {
        case 'select':
          draw.current.changeMode('simple_select');
          break;
        case 'polygon':
          draw.current.changeMode('draw_polygon');
          break;
        case 'line':
          draw.current.changeMode('draw_line_string');
          break;
        case 'point':
          draw.current.changeMode('draw_point');
          break;
        default:
          draw.current.changeMode('simple_select');
      }
    }
  }, [drawingMode]);

  // Handle initial view
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    if (initialView.bounds) {
      map.current.fitBounds(initialView.bounds, { padding: 50 });
    } else if (initialView.center && initialView.zoom) {
      map.current.setCenter(initialView.center);
      map.current.setZoom(initialView.zoom);
    }
  }, [mapLoaded, initialView]);

  // Add layers
  useEffect(() => {
    if (!map.current || !mapLoaded || !mapReady) return;

    layers.forEach(layer => {
      if (!map.current.getLayer(layer.id)) {
        map.current.addLayer(layer);
      }
    });
  }, [mapLoaded, mapReady, layers]);

  // Inactivity timer: turn parcels OFF after 90s without interaction
  useEffect(() => {
    if (!showRegridParcels || !map.current) return;
    let last = Date.now();
    const bump = () => { last = Date.now(); };
    const events = ['move', 'moveend', 'zoom', 'zoomend', 'mousemove', 'click'];
    try { events.forEach(ev => map.current.on(ev, bump)); } catch (_) {}
    const timer = setInterval(() => {
      if (!showRegridParcels) return;
      if (Date.now() - last > 90000) {
        setShowRegridParcels(false);
        try { showToast && showToast({ title: 'Parcels off', body: 'Turned off due to inactivity', variant: 'info' }); } catch (_) {}
      }
    }, 10000);
    return () => {
      clearInterval(timer);
      try { events.forEach(ev => map.current.off(ev, bump)); } catch (_) {}
    };
  }, [showRegridParcels, showToast]);

  // AOI gating: allow only within AL/MS/TN/GA (coarse bbox)
  useEffect(() => {
    if (!AOI_GATING_ENABLED) return;
    if (!showRegridParcels || !map.current) return;
    try {
      const c = map.current.getCenter();
      const lng = c && c.lng, lat = c && c.lat;
      // SE states bbox: AL/MS/TN/GA
      const se = { west: -91.7, east: -80.5, south: 30.0, north: 36.8 };
      // Texas bbox
      const tx = { west: -106.7, east: -93.0, south: 25.5, north: 36.7 };
      const insideSe = (lng >= se.west && lng <= se.east && lat >= se.south && lat <= se.north);
      const insideTx = (lng >= tx.west && lng <= tx.east && lat >= tx.south && lat <= tx.north);
      const inside = insideSe || insideTx;
      if (!inside) {
        setShowRegridParcels(false);
        try { console.warn('Parcels are gated to AL/MS/TN/GA/TX for now.'); } catch (_) {}
      }
    } catch (_) {}
  }, [showRegridParcels, AOI_GATING_ENABLED]);

  // Per-session tile guard (~1000 tiles)
  useEffect(() => {
    if (!TILE_GUARD_ENABLED || !showRegridParcels || !map.current) return;
    let count = 0;
    const hardCap = 1000;
    const handler = (e) => {
      try {
        if (e && isParcelTileSourceId(e.sourceId) && e.tile) {
          count += 1;
          if (count >= hardCap) {
            setShowRegridParcels(false);
            try { showToast && showToast({ title: 'Tile limit reached', body: 'Parcels turned off to control usage', variant: 'warning' }); } catch (_) {}
          }
        }
      } catch (_) {}
    };
    try { map.current.on('sourcedata', handler); } catch (_) {}
    return () => { try { map.current.off('sourcedata', handler); } catch (_) {} };
  }, [showRegridParcels, TILE_GUARD_ENABLED, showToast]);

  /**
   * Single owner for "user picked this parcel", whatever the input surface:
   * map click, parcel layer click, or the mobile centre crosshair. Held in a
   * ref because the layer handlers are bound once at map load and would
   * otherwise capture the selection mode that was active back then.
   */
  const dispatchParcelRef = useRef(null);
  useEffect(() => {
    dispatchParcelRef.current = (parcel) => {
      if (!parcel) return false;
      if (parcelSelectionEnabled && typeof onParcelSelectToggle === 'function') {
        onParcelSelectToggle(parcel);
        return true;
      }
      if (typeof onParcelInfoRequest === 'function') {
        onParcelInfoRequest(parcel);
        return true;
      }
      return false;
    };
  }, [parcelSelectionEnabled, onParcelSelectToggle, onParcelInfoRequest]);

  /**
   * Identify mode is read through the map instance so the canvas listeners,
   * which are bound once, always see the current mode.
   */
  useEffect(() => {
    if (!map.current) return;
    try { map.current.__yona_parcelIdentify = !!parcelIdentifyEnabled; } catch (_) {}
  }, [parcelIdentifyEnabled, mapReady]);

  // Same reason: lot-selection is read off the instance so toggling it does not
  // rebind the parcel handlers.
  useEffect(() => {
    if (!map.current) return;
    try { map.current.__yona_parcelSelecting = !!parcelSelectionEnabled; } catch (_) {}
  }, [parcelSelectionEnabled, mapReady]);

  // Let the parent offer Identify only while parcel boundaries are drawn.
  useEffect(() => {
    if (typeof onParcelsVisibilityChange === 'function') {
      onParcelsVisibilityChange(!!showRegridParcels);
    }
  }, [showRegridParcels, onParcelsVisibilityChange]);

  // Land Records parcels: add/remove vector source + line layer on toggle
  const ensureRegridLayersAdded = useCallback(() => {
    if (!map.current) return;
    const overlay = getParcelOverlayConfig();
    const sources = overlaySources(overlay);
    if (!sources.length) {
      try { showToast && showToast({ title: 'Parcels unavailable', body: 'Parcel tiles are not configured', variant: 'danger' }); } catch (_) {}
      try { setShowRegridParcels(false); } catch (_) {}
      return;
    }
    try {
      // Debug: surface basic state when enabling parcels
      try {
        const dbg = (typeof window !== 'undefined') && (new URLSearchParams(window.location.search || '').get('parcel_debug') === '1');
        if (dbg) {
          // eslint-disable-next-line no-console
          console.log('[parcels] ensure layers: zoom=', (map.current && map.current.getZoom && map.current.getZoom()), 'sources=', sources.map((s) => s.tilesUrl));
        }
      } catch (_) {}
      const minzoom = overlay.minzoom ?? PARCEL_PAINT_MIN_ZOOM;
      sources.forEach((source) => {
        const layerIds = parcelLayerIds(source.id);
        if (!map.current.getSource(source.id)) {
          map.current.addSource(source.id, {
            type: 'vector',
            tiles: [source.tilesUrl],
            minzoom,
            maxzoom: overlay.maxzoom,
            promoteId: overlay.promoteId
          });
        }
        if (!map.current.getLayer(layerIds.outline)) {
          map.current.addLayer({
            id: layerIds.outline,
            type: 'line',
            source: source.id,
            'source-layer': 'parcels',
            minzoom,
            paint: {
              'line-color': '#ffff00',
              'line-width': 1.75,
              'line-opacity': 0.95
            },
            layout: {
              'line-cap': 'round',
              'line-join': 'round'
            }
          });
        }
        if (!map.current.getLayer(layerIds.fillInteractive)) {
          map.current.addLayer({
            id: layerIds.fillInteractive,
            type: 'fill',
            source: source.id,
            'source-layer': 'parcels',
            minzoom,
            paint: {
              'fill-color': '#ffffff',
              'fill-opacity': 0.01
            }
          }, layerIds.outline);
        }
        if (!map.current.getLayer(layerIds.highlight)) {
          map.current.addLayer({
            id: layerIds.highlight,
            type: 'line',
            source: source.id,
            'source-layer': 'parcels',
            minzoom,
            paint: {
              'line-color': '#ffffff',
              'line-width': 3,
              'line-opacity': [
                'case',
                ['any',
                  ['boolean', ['feature-state', 'hover'], false],
                  ['boolean', ['feature-state', 'selected'], false]
                ],
                1.0,
                0.0
              ],
            },
            layout: {
              'line-cap': 'round',
              'line-join': 'round'
            }
          });
        }
        if (!map.current.getLayer(layerIds.fillHighlight)) {
          map.current.addLayer({
            id: layerIds.fillHighlight,
            type: 'fill',
            source: source.id,
            'source-layer': 'parcels',
            minzoom,
            paint: {
              'fill-color': '#ffff00',
              'fill-opacity': [
                'case',
                ['any',
                  ['boolean', ['feature-state', 'hover'], false],
                  ['boolean', ['feature-state', 'selected'], false]
                ],
                0.3,
                0.0
              ]
            }
          }, layerIds.highlight);
        }
        try {
          if (map.current.__yona_onParcelClick && typeof map.current.on === 'function') {
            try { map.current.off && map.current.off('click', layerIds.outline, map.current.__yona_onParcelClick); } catch (_) {}
            map.current.on('click', layerIds.outline, map.current.__yona_onParcelClick);
            try { map.current.on('mouseenter', layerIds.outline, () => { try { map.current.getCanvas().style.cursor = 'pointer'; } catch(_) {} }); } catch(_) {}
            try { map.current.on('mouseleave', layerIds.outline, () => { try { map.current.getCanvas().style.cursor = ''; } catch(_) {} }); } catch(_) {}
          }
        } catch (_) {}
      });

      // Robust canvas-level handlers: hover highlight and click via queryRenderedFeatures
      const onMouseMove = (e) => {
        try {
          const hoverLayers = PARCEL_HIT_LAYERS.filter((id) => map.current.getLayer && map.current.getLayer(id));
          const feats = hoverLayers.length
            ? (map.current.queryRenderedFeatures(e.point, { layers: hoverLayers }) || [])
            : [];
          const f = feats[0];
          // Feature-state hover (prefer ll_uuid via promoteId)
          try {
            const prev = hoveredParcelIdRef.current;
            if (prev != null) {
              const prevSource = typeof prev === 'object' ? prev.source : 'regrid-parcels';
              const prevId = typeof prev === 'object' ? prev.id : prev;
              map.current.setFeatureState({ source: prevSource, sourceLayer: 'parcels', id: prevId }, { hover: false });
            }
            const fid = (f && (f.id != null ? f.id : (f.properties && (f.properties.lrid || f.properties.ll_uuid || f.properties.parcelid)))) || null;
            if (fid != null) {
              const sourceId = (f && f.source) || 'regrid-parcels';
              map.current.setFeatureState({ source: sourceId, sourceLayer: 'parcels', id: fid }, { hover: true });
              hoveredParcelIdRef.current = { source: sourceId, id: fid };
            } else {
              hoveredParcelIdRef.current = null;
            }
          } catch (_) {}
          try { map.current.getCanvas().style.cursor = f ? 'pointer' : ''; } catch(_) {}
          // Debug: log tile properties under cursor if parcel_debug=1
          try {
            const qs = typeof window !== 'undefined' ? (window.location.search || '') : '';
            const dbg = qs ? (new URLSearchParams(qs).get('parcel_debug') === '1') : false;
            if (dbg && f && f.properties) {
              // eslint-disable-next-line no-console
              console.log('[parcels][hover] parcel feature keys:', Object.keys(f.properties), f.properties);
            }
          } catch (_) {}
        } catch(_) {}
      };

      // Clear hover on mouseleave
      try {
        const clearHover = () => {
          try {
            const prev = hoveredParcelIdRef.current;
            if (prev != null) {
              const prevSource = typeof prev === 'object' ? prev.source : 'regrid-parcels';
              const prevId = typeof prev === 'object' ? prev.id : prev;
              map.current.setFeatureState({ source: prevSource, sourceLayer: 'parcels', id: prevId }, { hover: false });
              hoveredParcelIdRef.current = null;
            }
            map.current.getCanvas().style.cursor = '';
          } catch (_) {}
        };
        PARCEL_HIT_LAYERS.forEach((layerId) => {
          if (map.current.getLayer && map.current.getLayer(layerId)) {
            map.current.on('mouseleave', layerId, clearHover);
          }
        });
      } catch (_) {}

      const onPointerDown = (e) => {
        try {
          map.current.__yona_parcelPointerDown = e && e.point ? { x: e.point.x, y: e.point.y } : null;
        } catch (_) {
          try { map.current.__yona_parcelPointerDown = null; } catch (_) {}
        }
      };

      const onClickCanvas = (e) => {
        try {
          if (map.current && map.current.__yona_isDrawing) return;
          // Touch devices reach parcels through the touchend handler, which is
          // the sole owner there. Mapbox also synthesizes a click from a tap,
          // and letting both run would toggle a lot twice: select then
          // deselect, indistinguishable from a tap that did nothing.
          if (prefersTouchMapGestures()) return;
          if (!isTapNotDrag(map.current.__yona_parcelPointerDown, e && e.point)) return;
          const parcel = parcelAtPoint(map.current, e && e.point);
          if (!parcel) return;
          if (dispatchParcelRef.current) dispatchParcelRef.current(parcel);
        } catch(_) {}
      };

      // Identify mode: while armed, a tap resolves to the parcel underneath and
      // the saved-feature path stands down, so a boundary cannot eat the tap.
      //
      // Uses Mapbox's own touch events rather than raw canvas listeners, so the
      // hit-test point is produced by the same code that positions the layers.
      // Computing it ourselves from the canvas rect risks disagreeing with
      // Mapbox, which measures from the canvas *container*.
      let identifyTouchStart = null;
      const onIdentifyTouchStart = (e) => {
        if (!e || !e.point || (Array.isArray(e.points) && e.points.length !== 1)) {
          identifyTouchStart = null;
          return;
        }
        identifyTouchStart = { x: e.point.x, y: e.point.y, t: Date.now() };
      };
      const onIdentifyTouchEnd = (e) => {
        const start = identifyTouchStart;
        identifyTouchStart = null;
        try {
          if (!start || !map.current) return;
          // Serves both parcel modes: dispatch routes to lot-select or info.
          if (!map.current.__yona_parcelIdentify && !map.current.__yona_parcelSelecting) return;
          if (map.current.__yona_isDrawing) return;
          if (!e || !e.point) return;
          if (!isParcelTouchTap(start, e.point, Date.now() - start.t)) return;
          const parcel = parcelAtPoint(map.current, e.point);
          recordParcelTapDebug(map.current, e.point, parcel);
          if (!parcel) {
            let zoom = null;
            try { zoom = map.current.getZoom ? map.current.getZoom() : null; } catch (_) {}
            const tooFarOut = Number.isFinite(zoom) && zoom < PARCEL_PAINT_MIN_ZOOM;
            try {
              showToast && showToast({
                title: tooFarOut ? 'Zoom in for parcels' : 'No parcel there',
                body: tooFarOut
                  ? 'Parcel boundaries load once you zoom in closer.'
                  : 'Tap inside a parcel boundary.',
                variant: 'info'
              });
            } catch (_) {}
            return;
          }
          if (dispatchParcelRef.current) dispatchParcelRef.current(parcel);
        } catch (_) {}
      };

      // Store to detach later
      map.current.__yona_regridHandlers = map.current.__yona_regridHandlers || {};
      // Remove existing to avoid duplicates
      try {
        if (map.current.__yona_regridHandlers.onMouseMove) map.current.off('mousemove', map.current.__yona_regridHandlers.onMouseMove);
        if (map.current.__yona_regridHandlers.onClickCanvas) map.current.off('click', map.current.__yona_regridHandlers.onClickCanvas);
        if (map.current.__yona_regridHandlers.onPointerDown) {
          map.current.off('mousedown', map.current.__yona_regridHandlers.onPointerDown);
          map.current.off('touchstart', map.current.__yona_regridHandlers.onPointerDown);
        }
        // Selection-mode toggles re-run this effect; drop the previous listeners.
        detachParcelIdentifyHandlers(map.current);
      } catch(_) {}
      try { map.current.on('mousemove', onMouseMove); } catch(_) {}
      try { map.current.on('mousedown', onPointerDown); } catch(_) {}
      try { map.current.on('touchstart', onPointerDown); } catch(_) {}
      try { map.current.on('click', onClickCanvas); } catch(_) {}
      try { map.current.on('touchstart', onIdentifyTouchStart); } catch(_) {}
      try { map.current.on('touchend', onIdentifyTouchEnd); } catch(_) {}
      map.current.__yona_regridHandlers.onMouseMove = onMouseMove;
      map.current.__yona_regridHandlers.onClickCanvas = onClickCanvas;
      map.current.__yona_regridHandlers.onPointerDown = onPointerDown;
      map.current.__yona_regridHandlers.identify = { onIdentifyTouchStart, onIdentifyTouchEnd };
    } catch (_) {}
    // Selection mode and the info handler are read through dispatchParcelRef,
    // so toggling them no longer needs to rebind the parcel handlers.
  }, [showToast]);

  const removeRegridLayers = useCallback(() => {
    if (!map.current) return;
    try {
      if (map.current.__yona_onParcelClick && typeof map.current.off === 'function') {
        PARCEL_SOURCE_IDS.forEach((sourceId) => {
          try { map.current.off('click', parcelLayerIds(sourceId).outline, map.current.__yona_onParcelClick); } catch (_) {}
        });
      }
      // Remove canvas handlers
      try {
        const h = map.current.__yona_regridHandlers || {};
        if (h.onMouseMove) map.current.off('mousemove', h.onMouseMove);
        if (h.onClickCanvas) map.current.off('click', h.onClickCanvas);
        if (h.onPointerDown) {
          map.current.off('mousedown', h.onPointerDown);
          map.current.off('touchstart', h.onPointerDown);
        }
        detachParcelIdentifyHandlers(map.current);
        map.current.__yona_regridHandlers = {};
      } catch(_) {}
      // Reset cursor
      try { map.current.getCanvas().style.cursor = ''; } catch(_) {}
      // Remove highlight layer then outline/source
      allParcelLayerIds(PARCEL_SOURCE_IDS).forEach((layerId) => {
        if (map.current.getLayer && map.current.getLayer(layerId)) {
          map.current.removeLayer(layerId);
        }
      });
      PARCEL_SOURCE_IDS.forEach((sourceId) => {
        if (map.current.getSource && map.current.getSource(sourceId)) {
          map.current.removeSource(sourceId);
        }
      });
      // Also remove any parcel selection preview overlays
      try {
        if (map.current.getLayer && map.current.getLayer('parcel-selection-preview-fill')) {
          map.current.removeLayer('parcel-selection-preview-fill');
        }
        if (map.current.getLayer && map.current.getLayer('parcel-selection-preview-outline')) {
          map.current.removeLayer('parcel-selection-preview-outline');
        }
        if (map.current.getSource && map.current.getSource('parcel-selection-preview')) {
          map.current.removeSource('parcel-selection-preview');
        }
      } catch (_) {}
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showRegridParcels) {
      try {
        const doAdd = () => {
          try {
            if (typeof map.current.isStyleLoaded === 'function' && !map.current.isStyleLoaded()) {
              map.current.once && map.current.once('idle', ensureRegridLayersAdded);
            } else {
              requestAnimationFrame(() => ensureRegridLayersAdded());
            }
          } catch (_) { ensureRegridLayersAdded(); }
        };
        doAdd();
      } catch (_) {}
    } else {
      removeRegridLayers();
    }
    // Keep control visual state in sync
    try {
      if (parcelsCtrlRef.current) {
        // Refresh getters so the control reads the latest React state
        parcelsCtrlRef.current.getParcelsOn = () => !!showRegridParcels;
        parcelsCtrlRef.current.toggleParcels = (next) => {
          if (requestParcelsToggleRef.current) requestParcelsToggleRef.current(next);
        };
        parcelsCtrlRef.current.getSelectionOn = () => !!parcelSelectionEnabled;
        parcelsCtrlRef.current.getHasSelection = () => !!hasParcelSelection;
        parcelsCtrlRef.current.getIsBusy = () => !!isParcelCreateBusy;
        parcelsCtrlRef.current.getPublicLandsOn = () => !!showPublicLands;
        // Keep the action callback fresh to avoid stale closures
        parcelsCtrlRef.current.onCreateOrSave = () => {
          try { console.log('[parcel][ui] forwarding action to parent'); } catch(_) {}
          try { typeof onRequestParcelBoundaryCreate === 'function' && onRequestParcelBoundaryCreate(); } catch(_) {}
        };
        parcelsCtrlRef.current.update && parcelsCtrlRef.current.update();
      }
    } catch(_) {}
  }, [mapLoaded, showRegridParcels, parcelSelectionEnabled, hasParcelSelection, isParcelCreateBusy, onRequestParcelBoundaryCreate, ensureRegridLayersAdded, removeRegridLayers, showPublicLands, canEnableParcels]);

  // Keep labels (reference overlay) in sync with state and control UI
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    try {
      if (typeof map.current.__yona_setReferenceOverlay === 'function') {
        map.current.__yona_setReferenceOverlay(!!showReferenceLabels);
      }
      if (parcelsCtrlRef.current) {
        parcelsCtrlRef.current.getLabelsOn = () => !!showReferenceLabels;
        parcelsCtrlRef.current.update && parcelsCtrlRef.current.update();
      }
      // Sync 3D control visual state
      if (map.current.__yona_viewCtrl && typeof map.current.__yona_viewCtrl.setOn === 'function') {
        map.current.__yona_viewCtrl.setOn(!!is3D);
      }
    } catch (_) {}
  }, [mapLoaded, showReferenceLabels, is3D]);

  useEffect(() => {
    if (parcelsCtrlRef.current && typeof parcelsCtrlRef.current.update === 'function') {
      parcelsCtrlRef.current.update();
    }
  }, [showPublicLands]);

  // Public lands overlay
  const ensurePublicLandsLayersAdded = useCallback(() => {
    if (!map.current) return;
    const base = process.env.REACT_APP_PUBLIC_LANDS_TILES_BASE || '';
    if (!base) {
      try { showToast && showToast({ title: 'Public lands unavailable', body: 'Missing tiles base URL', variant: 'danger' }); } catch (_) {}
      setShowPublicLands(false);
      return;
    }
    const sourceId = 'public-lands';
    const fillId = 'public-lands-fill';
    const lineId = 'public-lands-line';
    try {
      if (!map.current.getSource(sourceId)) {
        map.current.addSource(sourceId, {
          type: 'vector',
          tiles: [`${base}/{z}/{x}/{y}.pbf`],
          minzoom: 4,
          maxzoom: 13
        });
      }
      if (!map.current.getLayer(fillId)) {
        map.current.addLayer({
          id: fillId,
          type: 'fill',
          source: sourceId,
          'source-layer': 'public_lands',
          paint: {
            'fill-color': PUBLIC_LANDS_COLOR_EXPRESSION,
            'fill-opacity': 0.35
          }
        });
      }
      if (!map.current.getLayer(lineId)) {
        map.current.addLayer({
          id: lineId,
          type: 'line',
          source: sourceId,
          'source-layer': 'public_lands',
          paint: {
            'line-color': PUBLIC_LANDS_COLOR_EXPRESSION,
            'line-width': 0.8
          }
        });
      }
      if (!publicLandsClickHandlerRef.current) {
        const handler = (e) => {
          try {
            const f = (e && e.features && e.features[0]) || null;
            const p = (f && f.properties) || {};
            const acres = Number(p.GIS_Acres);
            const acresDisplay = Number.isFinite(acres) && acres > 0
              ? `${Math.round(acres).toLocaleString()} ac`
              : null;
            const html = buildPopupHtml(
              p.Unit_Nm || 'Unnamed tract',
              [
                { label: 'Type', value: p.Des_Tp || '—' },
                { label: 'Managed/Owned by', value: p.Mang_Name || p.Own_Name || '—' },
                { label: 'Access', value: p.Pub_Access || '—' },
                { label: 'State / Acres', value: `${p.State_Nm || '—'}${acresDisplay ? ` · ${acresDisplay}` : ''}` }
              ]
            );
            new mapboxgl.Popup({ closeButton: true, closeOnClick: true, className: 'yona-popup' })
              .setLngLat(e.lngLat).setHTML(html).addTo(map.current);
          } catch (_) {}
        };
        publicLandsClickHandlerRef.current = handler;
        try { map.current.on('click', fillId, handler); } catch (_) {}
        try { map.current.on('click', lineId, handler); } catch (_) {}
      }
    } catch (_) {}
  }, [setShowPublicLands, showToast]);

  const removePublicLandsLayers = useCallback(() => {
    if (!map.current) return;
    try {
      if (publicLandsClickHandlerRef.current) {
        try { map.current.off('click', 'public-lands-fill', publicLandsClickHandlerRef.current); } catch (_) {}
        try { map.current.off('click', 'public-lands-line', publicLandsClickHandlerRef.current); } catch (_) {}
        publicLandsClickHandlerRef.current = null;
      }
      if (map.current.getLayer && map.current.getLayer('public-lands-line')) map.current.removeLayer('public-lands-line');
      if (map.current.getLayer && map.current.getLayer('public-lands-fill')) map.current.removeLayer('public-lands-fill');
      if (map.current.getSource && map.current.getSource('public-lands')) map.current.removeSource('public-lands');
    } catch (_) {}
  }, []);

  const ensureSoilLayersAdded = useCallback(() => {
    if (!map.current) return;
    try {
      const styleLoaded = typeof map.current.isStyleLoaded === 'function' ? map.current.isStyleLoaded() : true;
      if (!styleLoaded) return;
      if (!map.current.getSource(SOIL_SOURCE_ID)) {
        map.current.addSource(SOIL_SOURCE_ID, {
          type: 'vector',
          tiles: [SOIL_TILE_URL],
          minzoom: 5,
          maxzoom: 14
        });
      }
      const paint = {
        'fill-color': soilColorMode === 'texture' ? SOIL_TEXTURE_COLORS : SOIL_DRAINAGE_COLORS,
        'fill-opacity': soilColorMode === 'texture' ? 0.6 : 0.5
      };
      if (!map.current.getLayer(SOIL_LAYER_ID)) {
        map.current.addLayer({
          id: SOIL_LAYER_ID,
          type: 'fill',
          source: SOIL_SOURCE_ID,
          'source-layer': 'soil_mu',
          paint
        });
      } else {
        map.current.setPaintProperty(SOIL_LAYER_ID, 'fill-color', paint['fill-color']);
        map.current.setPaintProperty(SOIL_LAYER_ID, 'fill-opacity', paint['fill-opacity']);
      }
      // Remove outline layer if present to keep fills more prominent
      if (map.current.getLayer && map.current.getLayer(SOIL_LINE_ID)) {
        try { map.current.removeLayer(SOIL_LINE_ID); } catch (_) {}
      }
      if (!soilClickHandlerRef.current) {
        const clickHandler = (e) => {
          try {
            if (!e.features || e.features.length === 0) return;
            const f = e.features[0];
            const p = (f && f.properties) || {};
            const drainageRaw = p.drainage_class;
            const textureRaw = p.texture_class;
            const drainageNormPreset = p.drainage_class_normalized || normalizeDrainageLocal(drainageRaw, p.hydrologic_group);
            const textureNormPreset = p.texture_class_normalized || normalizeTextureLocal(textureRaw);
            const drainageNorm = drainageNormPreset ? (DRAINAGE_LABELS[drainageNormPreset] || drainageNormPreset) : null;
            const textureNorm = textureNormPreset ? (TEXTURE_LABELS[textureNormPreset] || textureNormPreset) : null;
            const hydgrp = p.hydrologic_group || '—';
            const ph = p.ph !== undefined && p.ph !== null ? Number(p.ph).toFixed(1) : '—';
            const fmt = (norm, raw, fallback) => {
              const normPart = norm ? `(${norm})` : '';
              const rawPart = raw || '';
              const combined = [normPart, rawPart].filter(Boolean).join(' ').trim();
              return combined || fallback;
            };
            const drainageDisplay = drainageNorm || drainageRaw || '—';
            const textureDisplay = fmt(textureNorm, textureRaw, '—');
            const html = buildPopupHtml(
              `${p.musym || 'Soil unit'}${p.muname ? ` – ${p.muname}` : ''}`,
              [
                { label: 'Drainage', value: drainageDisplay },
                { label: 'Type', value: textureDisplay },
                { label: 'Hydrologic group', value: hydgrp },
                { label: 'pH', value: ph }
              ]
            );
            if (!soilPopupRef.current) {
              soilPopupRef.current = new mapboxgl.Popup({ closeButton: true, closeOnClick: false, className: 'yona-popup' });
            }
            soilPopupRef.current.setLngLat(e.lngLat).setHTML(html).addTo(map.current);
          } catch (_) {}
        };
        soilClickHandlerRef.current = clickHandler;
        try { map.current.on('click', SOIL_LAYER_ID, clickHandler); } catch (_) {}
      }
    } catch (_) {}
  }, [soilColorMode]);

  const removeSoilLayers = useCallback(() => {
    if (!map.current) return;
    try {
      if (soilClickHandlerRef.current) {
        try { map.current.off('click', SOIL_LAYER_ID, soilClickHandlerRef.current); } catch (_) {}
        soilClickHandlerRef.current = null;
      }
      if (soilPopupRef.current) {
        try { soilPopupRef.current.remove(); } catch (_) {}
        soilPopupRef.current = null;
      }
      if (map.current.getLayer && map.current.getLayer(SOIL_LINE_ID)) map.current.removeLayer(SOIL_LINE_ID);
      if (map.current.getLayer && map.current.getLayer(SOIL_LAYER_ID)) map.current.removeLayer(SOIL_LAYER_ID);
      if (map.current.getSource && map.current.getSource(SOIL_SOURCE_ID)) map.current.removeSource(SOIL_SOURCE_ID);
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showPublicLands) {
      if (typeof map.current.isStyleLoaded === 'function' && !map.current.isStyleLoaded()) {
        map.current.once && map.current.once('idle', ensurePublicLandsLayersAdded);
      } else {
        requestAnimationFrame(() => ensurePublicLandsLayersAdded());
      }
    } else {
      removePublicLandsLayers();
    }
  }, [mapLoaded, showPublicLands, ensurePublicLandsLayersAdded, removePublicLandsLayers]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showSoil) {
      const run = () => ensureSoilLayersAdded();
      if (typeof map.current.isStyleLoaded === 'function' && !map.current.isStyleLoaded()) {
        map.current.once && map.current.once('idle', run);
      } else {
        requestAnimationFrame(run);
      }
    } else {
      removeSoilLayers();
    }
  }, [mapLoaded, showSoil, soilColorMode, ensureSoilLayersAdded, removeSoilLayers]);

  useEffect(() => {
    try { parcelsCtrlRef.current && parcelsCtrlRef.current.update && parcelsCtrlRef.current.update(); } catch (_) {}
  }, [showSoil, soilColorMode]);

  // Close Parcels popover when signaled by parent
  useEffect(() => {
    try {
      const ctrl = parcelsCtrlRef.current;
      if (ctrl && ctrl._open) {
        ctrl._open = false;
        if (typeof ctrl._applyOpen === 'function') ctrl._applyOpen();
      }
    } catch (_) {}
  }, [closeParcelsPopoverSignal]);

  // Maintain multi-select highlight across selectedParcelIds
  const prevSelectedRef = useRef(new Set());
  useEffect(() => {
    try {
      const liveSources = PARCEL_SOURCE_IDS.filter((sourceId) => map.current.getSource && map.current.getSource(sourceId));
      if (!map.current || !liveSources.length) return;
      const next = new Set((selectedParcelIds || []).map(String));
      const prev = prevSelectedRef.current;
      const setSelected = (id, selected) => {
        liveSources.forEach((sourceId) => {
          try { map.current.setFeatureState({ source: sourceId, sourceLayer: 'parcels', id }, { selected }); } catch (_) {}
        });
      };
      prev.forEach((id) => {
        if (!next.has(id)) setSelected(id, false);
      });
      next.forEach((id) => {
        if (!prev.has(id)) setSelected(id, true);
      });
      prevSelectedRef.current = next;
    } catch (_) {}
  }, [selectedParcelIds, mapLoaded]);

  // Preview geometry overlay (Feature or FeatureCollection)
  useEffect(() => {
    try {
      if (!map.current || !mapLoaded) return;
      if (!previewUnionGeometry) {
        try {
          if (map.current.getLayer('parcel-selection-preview-outline')) map.current.removeLayer('parcel-selection-preview-outline');
          if (map.current.getLayer('parcel-selection-preview-fill')) map.current.removeLayer('parcel-selection-preview-fill');
          if (map.current.getSource('parcel-selection-preview')) map.current.removeSource('parcel-selection-preview');
        } catch (_) {}
        return;
      }
      const data = (previewUnionGeometry && previewUnionGeometry.type === 'FeatureCollection')
        ? previewUnionGeometry
        : { type: 'Feature', properties: {}, geometry: previewUnionGeometry };
      if (!map.current.getSource('parcel-selection-preview')) {
        map.current.addSource('parcel-selection-preview', { type: 'geojson', data });
      } else {
        map.current.getSource('parcel-selection-preview').setData(data);
      }
      if (!map.current.getLayer('parcel-selection-preview-fill')) {
        map.current.addLayer({
          id: 'parcel-selection-preview-fill',
          type: 'fill',
          source: 'parcel-selection-preview',
          paint: { 'fill-color': '#00ff00', 'fill-opacity': 0.12 }
        });
      }
      if (!map.current.getLayer('parcel-selection-preview-outline')) {
        map.current.addLayer({
          id: 'parcel-selection-preview-outline',
          type: 'line',
          source: 'parcel-selection-preview',
          paint: { 'line-color': '#00ff00', 'line-width': 2.0 }
        });
      }
    } catch (_) {}
  }, [previewUnionGeometry, mapLoaded]);
  // If URL contains a parcel identifier, auto-enable parcels and highlight exact parcel
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    try {
      const qs = typeof window !== 'undefined' ? window.location.search || '' : '';
      if (!qs) return;
      const params = new URLSearchParams(qs);
      const ll = params.get('parcel_ll');       // ll_uuid
      const pid = params.get('parcel_id');      // parcelnumb/fid/ogc_fid etc.
      const ppath = params.get('parcel_path');  // /us/xx/county/...
      const centerStr = params.get('parcel_center'); // lng,lat
      const target = ll || pid || ppath;
      if (!target) return;
      // Ensure layers exist
      setShowRegridParcels(true);
      ensureRegridLayersAdded();
      // If center provided, gently move there
      if (centerStr) {
        const arr = centerStr.split(',').map(Number);
        if (arr.length === 2 && !arr.some((n) => Number.isNaN(n))) {
          try { map.current.easeTo({ center: arr, zoom: Math.max(15, (map.current.getZoom ? map.current.getZoom() : 15)), duration: 600 }); } catch (_) {}
        }
      }
      // Use feature-state 'selected' via promoteId when ll_uuid present; else fallback to property filter
      const applySelected = () => {
        try {
          const liveSources = PARCEL_SOURCE_IDS.filter((sourceId) => map.current && map.current.getSource && map.current.getSource(sourceId));
          if (!liveSources.length) return false;
          // If ll provided, use it; else do nothing (fallback highlight not supported here)
          const useId = ll || null;
          // Clear previous selection
          const prev = selectedParcelIdRef.current;
          const setSelectedOnSources = (id, selected) => {
            liveSources.forEach((sourceId) => {
              try { map.current.setFeatureState({ source: sourceId, sourceLayer: 'parcels', id }, { selected }); } catch (_) {}
            });
          };
          if (prev != null) {
            setSelectedOnSources(prev, false);
          }
          if (useId) {
            setSelectedOnSources(useId, true);
            selectedParcelIdRef.current = useId;
          } else {
            // Fallback: filter highlight layers by known property keys
            const value = pid || ppath;
            if (value) {
              const isNumeric = (() => /^[0-9]+$/.test(String(value).trim()))();
              const clauses = [
                ['==', ['get', 'parcelnumb'], value],
                ['==', ['get', 'parcel_id'], value],
                ['==', ['get', 'account_number'], value],
                ['==', ['get', 'path'], value]
              ];
              if (isNumeric) {
                clauses.push(['==', ['get', 'ogc_fid'], ['to-number', value]]);
                clauses.push(['==', ['get', 'fid'], ['to-number', value]]);
                clauses.push(['==', ['get', 'id'], ['to-number', value]]);
                clauses.push(['==', ['get', 'objectid'], ['to-number', value]]);
              }
              const filter = ['any', ...clauses];
              liveSources.forEach((sourceId) => {
                const ids = parcelLayerIds(sourceId);
                try { map.current.setFilter(ids.fillHighlight, filter); } catch (_) {}
                try { map.current.setFilter(ids.highlight, filter); } catch (_) {}
              });
            }
            selectedParcelIdRef.current = null;
          }
          // Debug
          try {
            const qs2 = typeof window !== 'undefined' ? (window.location.search || '') : '';
            const dbg = qs2 ? (new URLSearchParams(qs2).get('parcel_debug') === '1') : false;
            if (dbg) {
              // eslint-disable-next-line no-console
              console.log('[regrid][highlight] selected feature-state for:', useId || '(none)');
            }
          } catch (_) {}
          return true;
        } catch (_) {
          return false;
        }
      };
      if (!applySelected()) {
        setTimeout(applySelected, 150);
        setTimeout(applySelected, 350);
      }
    } catch (_) {}
  }, [mapLoaded, ensureRegridLayersAdded]);

  // Render features on the map - use a separate effect that doesn't cause re-initialization
  useEffect(() => {
    if (!map.current || !mapLoaded || !mapReady) return;

    const currentFeatures = featuresRef.current;
    if (!currentFeatures || currentFeatures.length === 0) return;

    console.log('Attempting to render features:', currentFeatures.length, 'features');
    console.log('Map loaded:', mapLoaded, 'Map ready:', mapReady);

    // Use a timeout to ensure the map is fully ready
    const renderFeatures = () => {
      if (!map.current) return;

      console.log('Rendering features on map:', currentFeatures);

      // Avoid removing and re-adding layers/sources on each update to prevent flicker.

      // Separate features by type
      const polygons = currentFeatures.filter(f => f.geojson?.type === 'Polygon' || f.geojson?.type === 'MultiPolygon');
      const lines = currentFeatures.filter(f => f.geojson?.type === 'LineString');
      const points = currentFeatures.filter(f => f.geojson?.type === 'Point');

      console.log('Feature breakdown:', { polygons: polygons.length, lines: lines.length, points: points.length });

      // Add polygon features
      if (polygons.length > 0) {
        const polygonSource = {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: polygons.map(feature => ({
              type: 'Feature',
              id: feature.id,
              geometry: feature.geojson,
              properties: {
                id: feature.id,
                name: feature.name,
                type: feature.type,
                featureType: feature.featureType,
                acreage: feature.acreage,
                  notes: feature.notes,
                  // Optional style props provided by backend; used when present
                  lineColor: feature.line_color || feature.lineColor || null,
                  fillColor: feature.fill_color || feature.fillColor || null,
                  fillOpacity: typeof feature.fill_opacity === 'number' ? feature.fill_opacity : (typeof feature.fillOpacity === 'number' ? feature.fillOpacity : null)
              }
            }))
          }
        };

        console.log('Polygon source data:', polygonSource);

        try {
          const sourceId = 'features-polygons';
          const fillLayerId = 'features-polygons';
          const outlineLayerId = 'features-polygons-outline';
          const themeCream = getComputedStyle(document.documentElement).getPropertyValue('--color-cream').trim() || '#f6f5f2';
          const fillColorExpr = ['coalesce', ['get', 'fillColor'], themeCream];
          const fillOpacityExpr = ['coalesce', ['get', 'fillOpacity'], 0];
          const lineColorExpr = ['coalesce', ['get', 'lineColor'], themeCream];

          if (map.current.getSource(sourceId)) {
            map.current.getSource(sourceId).setData(polygonSource.data);
          } else {
            map.current.addSource(sourceId, polygonSource);
          }
          try {
            const sample = polygonSource.data.features && polygonSource.data.features[0] && polygonSource.data.features[0].properties;
            if (sample) {
              console.log('Polygon style sample props:', {
                lineColor: sample.lineColor,
                fillColor: sample.fillColor,
                fillOpacity: sample.fillOpacity
              });
            }
          } catch(_) {}
          // Ensure fill layer exists or update its paint
          if (map.current.getLayer(fillLayerId)) {
            map.current.setPaintProperty(fillLayerId, 'fill-color', fillColorExpr);
            map.current.setPaintProperty(fillLayerId, 'fill-opacity', fillOpacityExpr);
          } else {
            map.current.addLayer({
              id: fillLayerId,
              type: 'fill',
              source: sourceId,
              paint: {
                'fill-color': fillColorExpr,
                'fill-opacity': fillOpacityExpr
              }
            });
          }

          // Ensure outline layer exists or update its paint
          if (map.current.getLayer(outlineLayerId)) {
            map.current.setPaintProperty(outlineLayerId, 'line-color', lineColorExpr);
            map.current.setPaintProperty(outlineLayerId, 'line-width', 4);
            map.current.setPaintProperty(outlineLayerId, 'line-opacity', 1);
          } else {
            map.current.addLayer({
              id: outlineLayerId,
              type: 'line',
              source: sourceId,
              paint: {
                'line-color': lineColorExpr,
                'line-width': 4,
                'line-opacity': 1
              }
            });
          }
          console.log('Polygon features added successfully');
        } catch (error) {
          console.error('Error adding polygon features:', error);
        }
      }

      // Add line features
      if (lines.length > 0) {
        const lineSource = {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: lines.map(feature => ({
              type: 'Feature',
              id: feature.id,
              geometry: feature.geojson,
              properties: {
                id: feature.id,
                name: feature.name,
                type: feature.type,
                length: feature.length,
                notes: feature.notes
              }
            }))
          }
        };

        try {
          if (map.current.getSource('features-lines')) {
            map.current.getSource('features-lines').setData(lineSource.data);
          } else {
            map.current.addSource('features-lines', lineSource);
          }
          map.current.addLayer({
            id: 'features-lines',
            type: 'line',
            source: 'features-lines',
            paint: {
              'line-color': '#ffff00', // Changed from '#28a745' (green) to '#ffff00' (yellow)
              'line-width': 3,
              'line-opacity': 1
            }
          });
          console.log('Line features added successfully');
        } catch (error) {
          console.error('Error adding line features:', error);
        }
      }

      // Add point features
      if (points.length > 0) {
        const pointSource = {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: points.map((feature) => ({
              type: 'Feature',
              id: feature.id,
              geometry: feature.geojson,
              properties: buildMapPointProperties(feature, currentWindDegreesRef.current)
            }))
          }
        };

        try {
          // Ensure default marker and stand icon images exist
          if (!map.current.hasImage('marker-15')) {
            // rely on mapbox built-in sprite name; many styles already include it
          }
          if (!map.current.hasImage('stand-icon')) {
            // placeholder: reuse marker-15 as stand-icon until real asset provided
            // For safety, attempt to use existing marker as alias by adding same image data is non-trivial here;
            // consumers may set a proper image via style sprite; skip if unavailable.
          }

          if (map.current.getSource('features-points')) {
            map.current.getSource('features-points').setData(pointSource.data);
          } else {
            map.current.addSource('features-points', pointSource);
          }
          // Stand pins vs markers: point_role from is_stand / feeding, not marker_type.
          const pointNoMarkerFilter = STAND_POINT_FILTER;
          // Circle status layer (stands only)
          if (!map.current.getLayer('features-points')) map.current.addLayer({
            id: 'features-points',
            type: 'circle',
            source: 'features-points',
            filter: pointNoMarkerFilter,
            paint: fieldEditionStandPins
              ? {
                  'circle-color': [
                    'case',
                    ['boolean', ['get', 'isOccupied'], false], '#B4501C',
                    ['==', ['get', 'isOptimalWind'], ['literal', true]], '#3B4E3A',
                    '#F0EADA'
                  ],
                  'circle-radius': [
                    'case',
                    ['boolean', ['get', 'isOccupied'], false], 10,
                    8
                  ],
                  'circle-stroke-color': '#221E18',
                  'circle-stroke-width': 3,
                  'circle-opacity': 1
                }
              : {
              'circle-color': [
                'case',
                // Occupied takes precedence (cast to boolean)
                ['boolean', ['get', 'isOccupied'], false], '#dc3545',
                // Then optimal wind highlight
                ['==', ['get', 'isOptimalWind'], ['literal', true]], '#28a745',
                // Default (blue for stands availability)
                '#007bff'
              ],
              'circle-radius': 8,
              'circle-stroke-color': '#fff',
              'circle-stroke-width': 2,
              'circle-blur': [
                'case',
                ['==', ['get', 'isOptimalWind'], ['literal', true]], 0.1,
                0
              ],
              'circle-opacity': 1
            }
          });

          // Feeding progress halo disabled to avoid clutter; wind halo remains.

          // Marker background circle (black) to differentiate markers
          if (!map.current.getLayer('features-marker-bg')) map.current.addLayer({
            id: 'features-marker-bg',
            type: 'circle',
            source: 'features-points',
            filter: MARKER_POINT_FILTER,
            paint: {
              'circle-color': '#000000',
              'circle-radius': [
                'interpolate', ['linear'], ['zoom'],
                10, 8,
                16, 9.6
              ],
              'circle-stroke-color': '#ffffff',
              'circle-stroke-width': 2,
              'circle-opacity': 1
            }
          });

          // Marker symbol layer (stand+feed stays a stand pin; feed-only uses feeder icon)
          if (!map.current.getLayer('features-markers')) {
            map.current.addLayer({
              id: 'features-markers',
              type: 'symbol',
              source: 'features-points',
              filter: MARKER_POINT_FILTER,
              layout: {
                'icon-image': ['get', 'marker_icon_id'],
                'icon-size': [
                  'interpolate', ['linear'], ['zoom'],
                  10, 1.0,
                  16, 1.2
                ],
                'icon-allow-overlap': true,
                'icon-anchor': 'center'
              },
              paint: {
                'icon-opacity': 1
              }
            });
            if (didInitialFadeRef.current) {
              try { map.current.setPaintProperty('features-markers', 'icon-opacity', 1); } catch (_) {}
            }
          }

          // Bind hover tooltip for markers (once)
          if (!markerHoverBoundRef.current) {
            if (typeof map.current.on === 'function') {
              map.current.on('mouseenter', 'features-markers', (e) => {
                map.current.getCanvas().style.cursor = 'pointer';
                const f = e.features && e.features[0];
                if (!f) return;
                const props = f.properties || {};
                const name = props.name || 'Unnamed';
                const typeRaw = props.marker_label || props.marker_type || props.markerType || '';
                const typeLabel = String(typeRaw).replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
                const coords = f.geometry && f.geometry.coordinates;
                try { if (hoverPopupRef.current) hoverPopupRef.current.remove(); } catch {}
                const popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false })
                  .setLngLat(coords)
                  .setHTML(`<div style="font-size:12px;line-height:1.2"><strong>${name}</strong><div>${typeLabel}</div></div>`)
                  .addTo(map.current);
                hoverPopupRef.current = popup;
              });
              map.current.on('mouseleave', 'features-markers', () => {
                map.current.getCanvas().style.cursor = '';
                try { if (hoverPopupRef.current) hoverPopupRef.current.remove(); } catch {}
                hoverPopupRef.current = null;
              });
              markerHoverBoundRef.current = true;
            }
          }

          // Wind halo wedges for selected optimal winds — add UNDER the circle and icon layers
          if (ENABLE_WIND_WEDGES && map.current.hasImage('wind-wedge-green')) {
            const baseLayout = {
              'icon-image': 'wind-wedge-green',
              'icon-size': 1.0,
              'icon-allow-overlap': true,
              'icon-anchor': 'center',
            };
            const glowLayout = {
              ...baseLayout,
              'icon-image': 'wind-wedge-green-glow'
            };
            const standFilter = ['all', pointNoMarkerFilter, ['==', ['get', 'stand_show'], 'true']];
            // Create 8 wedge layers around each stand; show wedge if that direction is in optimal_winds
            const directions = ['N','NE','E','SE','S','SW','W','NW'];
            const rotations = [0,45,90,135,180,225,270,315];
            directions.forEach((dir, idx) => {
              const layerId = `wind-wedge-${dir.toLowerCase()}`;
              const glowId = `wind-wedge-${dir.toLowerCase()}-glow`;
              try {
                if (!map.current.getLayer(layerId)) map.current.addLayer({
                  id: layerId,
                  type: 'symbol',
                  source: 'features-points',
                  filter: ['all', standFilter, ['in', dir, ['get', 'optimal_winds']]],
                  layout: {
                    ...baseLayout,
                    'icon-rotate': rotations[idx]
                  }
                }, 'features-points');
                if (!map.current.getLayer(glowId)) map.current.addLayer({
                  id: glowId,
                  type: 'symbol',
                  source: 'features-points',
                  filter: ['all', standFilter, ['all', ['in', dir, ['get', 'optimal_winds']], ['==', ['get', 'current_wind_cardinal'], dir]]],
                  layout: {
                    ...glowLayout,
                    'icon-rotate': rotations[idx]
                  }
                }, 'features-points');
              } catch (e) {
                // skip if already exists
              }
            });
          }

          // Wind halo + current direction arrow
          if (ENABLE_WIND_HALO_ARROW) {
            // Stand filter: use string property stand_show so filter has no boolean/expression the validator rejects
            const standFilterArrow = ['all', pointNoMarkerFilter, ['==', ['get', 'stand_show'], 'true']];
            // Halo circle under main circle (stands only)
            if (!map.current.getLayer('wind-halo')) {
              map.current.addLayer({
                id: 'wind-halo',
                type: 'circle',
                source: 'features-points',
                filter: standFilterArrow,
                paint: {
                  'circle-color': [
                    'case',
                    ['==', ['get', 'isOptimalWind'], ['literal', true]], '#28a745',
                    'rgba(255,255,255,0.6)'
                  ],
                  'circle-radius': [
                    'interpolate', ['linear'], ['zoom'],
                    10, 16,
                    16, 22
                  ],
                  'circle-blur': 0.35,
                  'circle-opacity': 1
                }
              }, 'features-points');
            } else {
              try { map.current.setFilter('wind-halo', standFilterArrow); } catch (_) {}
            }
            // Additional ring with margin using transparent center (symbol image)
            const ensureRingImages = () => {
              const makeRing = (id, strokeColor) => {
                if (map.current.hasImage(id)) return;
                const size = 80;
                const canvas = document.createElement('canvas');
                canvas.width = size; canvas.height = size;
                const ctx = canvas.getContext('2d');
                ctx.clearRect(0, 0, size, size);
                const cx = size / 2; const cy = size / 2;
                const outerR = 30; // outer radius
                const innerR = 18; // inner radius creates margin from main circle (~8px)
                // Draw outer circle
                ctx.beginPath();
                ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
                ctx.closePath();
                ctx.fillStyle = strokeColor;
                ctx.fill();
                // Punch inner hole
                ctx.globalCompositeOperation = 'destination-out';
                ctx.beginPath();
                ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
                ctx.closePath();
                ctx.fill();
                ctx.globalCompositeOperation = 'source-over';
                const imgData = ctx.getImageData(0, 0, size, size);
                map.current.addImage(id, { width: size, height: size, data: new Uint8Array(imgData.data) }, { pixelRatio: 2 });
              };
              makeRing('wind-ring-green', 'rgba(40,167,69,0.9)');
              makeRing('wind-ring-white', 'rgba(255,255,255,0.7)');
            };
            ensureRingImages();
            if (!map.current.getLayer('wind-ring-layer')) {
              map.current.addLayer({
                id: 'wind-ring-layer',
                type: 'symbol',
                source: 'features-points',
                filter: standFilterArrow,
                layout: {
                  'icon-image': [
                    'case',
                    ['==', ['get', 'isOptimalWind'], ['literal', true]], 'wind-ring-green',
                    'wind-ring-white'
                  ],
                  'icon-size': [
                    'interpolate', ['linear'], ['zoom'],
                    10, 0.8,
                    16, 1.0
                  ],
                  'icon-allow-overlap': true,
                  'icon-anchor': 'center'
                },
                paint: {
                  'icon-opacity': 1
                }
              }, 'features-marker-bg');
            } else {
              try { map.current.setFilter('wind-ring-layer', standFilterArrow); } catch (_) {}
            }
            // Ensure simple arrow images
            const ensureArrow = () => {
              const makeArrow = (id, color) => {
                if (map.current.hasImage(id)) return;
                const size = 40;
                const canvas = document.createElement('canvas');
                canvas.width = size; canvas.height = size;
                const ctx = canvas.getContext('2d');
                ctx.clearRect(0, 0, size, size);
                const cx = size / 2; const cy = size / 2;
                const innerR = 10; const outerR = 20;
                ctx.fillStyle = color;
                ctx.beginPath();
                ctx.moveTo(cx - 4, cy - innerR);
                ctx.lineTo(cx + 4, cy - innerR);
                ctx.lineTo(cx, cy - outerR);
                ctx.closePath();
                ctx.fill();
                const imgData = ctx.getImageData(0, 0, size, size);
                map.current.addImage(id, { width: size, height: size, data: new Uint8Array(imgData.data) }, { pixelRatio: 2 });
              };
              makeArrow('wind-arrow-grey-simple', 'rgba(180,180,180,0.95)');
              makeArrow('wind-arrow-green-simple', 'rgba(40,167,69,0.95)');
            };
            ensureArrow();
            if (windArrowDelayPassed) {
              if (!map.current.getLayer('wind-arrow-layer')) {
                map.current.addLayer({
                  id: 'wind-arrow-layer',
                  type: 'symbol',
                  source: 'features-points',
                  // Only show arrow when we have wind data on the feature
                  filter: ['all', standFilterArrow, ['has', 'current_wind_degrees'], ['!=', ['get', 'current_wind_degrees'], ['literal', null]]],
                  layout: {
                    'icon-rotation-alignment': 'map',
                    'icon-pitch-alignment': 'map',
                    'icon-image': [
                      'case',
                      ['==', ['get', 'isOptimalWind'], ['literal', true]], 'wind-arrow-green-simple',
                      'wind-arrow-grey-simple'
                    ],
                    'icon-rotate': [
                      '+',
                      // Base rotation in degrees
                      [
                        'case',
                        // If numeric degrees are provided and not null, use them
                        ['all', ['has', 'current_wind_degrees'], ['!=', ['get', 'current_wind_degrees'], ['literal', null]]],
                        ['to-number', ['get', 'current_wind_degrees']],
                        // Else, fallback: map cardinal to degrees (N=0, NE=45, ...); if missing, 0
                        [
                          'case',
                          ['in', ['get', 'current_wind_cardinal'], ['literal', ['N','NE','E','SE','S','SW','W','NW']]],
                          ['*', ['index-of', ['get', 'current_wind_cardinal'], ['literal', ['N','NE','E','SE','S','SW','W','NW']]], 45],
                          0
                        ]
                      ],
                      // Add 180° to point downwind (direction the wind is blowing toward)
                      180
                    ],
                    'icon-size': [
                      'interpolate', ['linear'], ['zoom'],
                      10, 1.4,
                      16, 2.0
                    ],
                    'icon-offset': [
                      'interpolate', ['linear'], ['zoom'],
                      10, ['literal', [0, -4]],
                      16, ['literal', [0, -8]]
                    ],
                    'icon-allow-overlap': true,
                    'icon-anchor': 'center'
                  }
                }, 'features-marker-bg');
              } else {
                try { map.current.setFilter('wind-arrow-layer', ['all', standFilterArrow, ['has', 'current_wind_degrees'], ['!=', ['get', 'current_wind_degrees'], ['literal', null]]]); } catch (_) {}
              }
            }
            // NSEW labels (visible at closer zooms)
            const addTextLayer = (id, text, offset) => {
              // Only add text layers if the current style provides glyphs
              const style = map.current && typeof map.current.getStyle === 'function' ? map.current.getStyle() : null;
              const hasGlyphs = Boolean(style && style.glyphs);
              if (!hasGlyphs) return;
              if (map.current.getLayer(id)) return;
              map.current.addLayer({
                id,
                type: 'symbol',
                source: 'features-points',
                minzoom: 12,
                filter: standFilterArrow,
                layout: {
                  'text-field': text,
                  'text-size': [
                    'interpolate', ['linear'], ['zoom'],
                    12, 9,
                    16, 12
                  ],
                  'text-offset': offset,
                  'text-anchor': 'center',
                  'text-allow-overlap': true
                },
                paint: {
                  'text-color': '#ffffff',
                  'text-halo-color': 'rgba(0,0,0,0.6)',
                  'text-halo-width': 1.2
                }
              }, map.current.getLayer('wind-arrow-layer') ? 'wind-arrow-layer' : 'features-marker-bg');
            };
            addTextLayer('wind-label-n', 'N', [0, -2.0]);
            addTextLayer('wind-label-e', 'E', [2.0, 0]);
            addTextLayer('wind-label-s', 'S', [0, 2.0]);
            addTextLayer('wind-label-w', 'W', [-2.0, 0]);
          }
          // Stand symbol layer (for stands only — uses stand_show string so filter passes validator)
          const standFilterStands = ['all', pointNoMarkerFilter, ['==', ['get', 'stand_show'], 'true']];
          if (map.current.hasImage('stand-icon') || map.current.hasImage('marker-15')) {
            if (!map.current.getLayer('features-stands')) {
              map.current.addLayer({
                id: 'features-stands',
                type: 'symbol',
                source: 'features-points',
                filter: standFilterStands,
                layout: {
                  'icon-image': map.current.hasImage('stand-icon') ? 'stand-icon' : 'marker-15',
                  'icon-size': 1.0,
                  'icon-allow-overlap': true,
                  'icon-anchor': 'center'
                },
                paint: {
                  'icon-opacity': 1
                }
              });
              if (didInitialFadeRef.current && map.current.getLayer('features-stands')) {
                try { map.current.setPaintProperty('features-stands', 'icon-opacity', 1); } catch (_) {}
              }
            }
          }
          console.log('Point features added successfully');
        } catch (error) {
          console.error('Error adding point features:', error);
        }
      }

      // One-time fade-in of feature layers to reduce jerkiness
      if (!didInitialFadeRef.current) {
        didInitialFadeRef.current = true;
        const fadeOps = [
          { id: 'features-points', prop: 'circle-opacity', to: 1 },
          { id: 'features-marker-bg', prop: 'circle-opacity', to: 1 },
          { id: 'features-markers', prop: 'icon-opacity', to: 1 },
          { id: 'features-stands', prop: 'icon-opacity', to: 1 },
          { id: 'features-lines', prop: 'line-opacity', to: 1 },
          { id: 'features-polygons-outline', prop: 'line-opacity', to: 1 },
          { id: 'wind-halo', prop: 'circle-opacity', to: 0.85 },
          { id: 'wind-ring-layer', prop: 'icon-opacity', to: 1 }
        ];
        try {
          fadeOps.forEach(({ id, prop, to }) => {
            try {
              if (map.current.getLayer(id)) {
                const transProp = `${prop}-transition`;
                try { map.current.setPaintProperty(id, transProp, { duration: 250, delay: 0 }); } catch {}
                map.current.setPaintProperty(id, prop, to);
              }
            } catch {}
          });
        } catch {}
      }

      // Notify that features have been rendered
      if (onFeaturesRendered) {
        onFeaturesRendered(currentFeatures);
      }
    };

    // Try to render immediately if map is ready
    if (mapReady) {
      renderFeatures();
    } else {
      // Wait a bit and try again
      setTimeout(renderFeatures, 1000);
    }

  }, [mapLoaded, mapReady, onFeaturesRendered, features, windArrowDelayPassed, ENABLE_WIND_HALO_ARROW, ENABLE_WIND_WEDGES, fieldEditionStandPins]);

  // Wind-only refresh: avoid rebuilding polygons/lines/layers when wind direction changes after pan
  useEffect(() => {
    if (!map.current || !mapLoaded || !mapReady) return;
    applyPointsWindToMap(map.current, featuresRef.current, currentWindDegrees);
  }, [currentWindDegrees, mapLoaded, mapReady]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div 
        ref={mapContainer} 
        style={{ 
          width: '100%', 
          height: '100%',
          position: 'relative'
        }}
      />
      {showParcelsControl ? (
      <button
        type="button"
        aria-label="Toggle Parcels"
        title="Toggle Parcels"
        onClick={() => requestParcelsToggleRef.current && requestParcelsToggleRef.current()}
        style={{
          position: 'absolute',
          top: 10,
          right: 10,
          zIndex: 2,
          display: parcelsCtrlAdded ? 'none' : 'inline-block'
        }}
        className="mapboxgl-ctrl-icon yona-ctrl-btn parcels-toggle-btn"
      >
        {showRegridParcels ? 'Parcels: On' : 'Parcels: Off'}
      </button>
      ) : null}
    </div>
  );
}